import { CommonModule } from '@angular/common';
import {
	Component,
	ViewChild,
	ViewContainerRef,
	TemplateRef,
	OnInit,
	OnDestroy,
	Injectable,
	ChangeDetectorRef,
	Injector,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
	CreatedShare,
	GameData,
	PluginType,
	RendererGameDefinitionService,
	PublicDataServiceProvider,
} from '@meta-press/shared-plugins';
import {
	NgbModal,
	NgbModalRef,
	NgbDatepickerModule,
	NgbAlertModule,
	NgbNavModule,
	NgbDropdownModule,
	NgbTooltipModule,
	NgbDateParserFormatter,
	NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import {
	Subscription,
	forkJoin,
	defer,
	tap,
	map,
	catchError,
	shareReplay,
	throwError,
	switchMap,
	of,
} from 'rxjs';
import { environment } from '../../../environments/environment';

import { Export } from '@meta-press/shared-plugins';

import { NotificationService } from '../notification-service/notification.service';
import { PDFService } from '../pdf-service/pdf.service';
// Add import for GridBoardRendererComponent
import { ExportService } from '../export-service/export.service';
import { SourceMaterialService } from '../source-material-service/source-material.service';
import { MigrationService } from '../migration-service/migration.service';
import { MigrationModalComponent } from '../migration-modal/migration-modal.component';
import { BackendService } from '../backend-service/backend.service';
import {
	BatchGenerationService,
	BatchJob,
} from '../services/batch-generation.service';
import { ExportNotificationModalComponent } from '../export-notification-modal/export-notification-modal.component';
import { ProgressWheelComponent } from '../progress-wheel/progress-wheel.component';
import { ShareModalComponent } from '../share-modal/share-modal.component';
import { ConfirmModalComponent } from '../components/confirm-modal/confirm-modal.component';

// Define a class to hold all data and state for a sharing operation
export class ShareProcess {
	public id = -1;
	public uuid = '';
	public name = '';
	public permissions = 'public'; // 'public' or 'restricted'
	public expirationDate = -1; // -1 means no expiration
	public isSharing = false;
	public isShared = false;
	public isError = false;
	public error: string | null = null; // This will hold any error that occurs during the sharing process
	public sharedUuid = '';
	public isDeleted = false;

	constructor(public exportData: Export) {
		this.name = exportData.name;
	}
}

@Injectable()
export class FinnishDateParserFormatter extends NgbDateParserFormatter {
	parse(value: string): NgbDateStruct | null {
		if (value) {
			const dateParts = value.trim().split('.');
			if (dateParts.length === 3) {
				const day = parseInt(dateParts[0], 10);
				const month = parseInt(dateParts[1], 10);
				const year = parseInt(dateParts[2], 10);
				if (day && month && year) {
					return { day, month, year };
				}
			}
		}
		return null;
	}

	format(date: NgbDateStruct | null): string {
		if (date) {
			return `${date.day}.${date.month}.${date.year}`;
		}
		return '';
	}
}

@Component({
	selector: 'app-exports',
	imports: [
		CommonModule,
		FormsModule,
		NgbDatepickerModule,
		NgbAlertModule,
		NgbNavModule,
		NgbDropdownModule,
		NgbTooltipModule,
		ProgressWheelComponent,
	], // Added NgbDatepickerModule and NgbAlertModule
	providers: [
		{
			provide: NgbDateParserFormatter,
			useClass: FinnishDateParserFormatter,
		},
	],
	templateUrl: './exports.component.html',
	styleUrl: './exports.component.scss',
	standalone: true,
})
export class ExportsComponent implements OnInit, OnDestroy {
	@ViewChild('shareModal') shareModalTemplate!: TemplateRef<unknown>;
	@ViewChild('deleteAllModal') deleteAllModalTemplate!: TemplateRef<unknown>;
	@ViewChild('renameModal') renameModalTemplate!: TemplateRef<unknown>;
	@ViewChild('deleteConfirmModal') deleteConfirmModal!: TemplateRef<unknown>;
	private shareModalRef: NgbModalRef | undefined;
	private deleteAllModalRef: NgbModalRef | undefined;
	private renameModalRef: NgbModalRef | undefined;

	// States for loading and errors

	protected hasError = false;
	protected errorMessage = '';

	// Track subscriptions for cleanup
	private subscriptions: Subscription[] = [];

	// Track which experiences are currently being deleted
	protected experiencesBeingDeleted: Set<number | string> = new Set<
		number | string
	>();

	// Track which batch jobs are currently being cancelled
	protected batchJobsBeingCancelled: Set<string> = new Set<string>();

	// Use a single object to manage the state of the active sharing process
	activeShareProcess: ShareProcess | null = null;

	// Separate properties for date and time inputs
	expirationDatePicker: { year: number; month: number; day: number } | null =
		null;
	expirationHour = 23;
	expirationMinute = 59;
	hasExpiryDate = false;

	// Store all shares for the current export being managed in the modal
	currentExportShares: CreatedShare[] = [];

	// Store the primary (most recent) share for the current export
	primaryShare: CreatedShare | null = null;

	// Store legacy shares (all except the most recent) for the current export
	legacyShares: CreatedShare[] = [];

	// Expiration date editing state
	isEditingExpirationDate = false;
	editExpirationDatePicker: {
		year: number;
		month: number;
		day: number;
	} | null = null;
	editExpirationHour = 23;
	editExpirationMinute = 59;
	editHasExpiryDate = false;
	shareBeingUpdated: number | null = null;

	// Track which shares are being deleted in the modal
	sharesBeingDeletedInModal: Set<number> = new Set<number>();

	// Track which shares are being loaded for preview in the modal
	sharesBeingLoadedInModal: Set<number> = new Set<number>();

	// Selected share for deletion confirmation
	selectedShareForDeletion: CreatedShare | null = null;

	// Selected export for deletion confirmation
	exportToDelete: Export | null = null;

	// Sorting properties for shares in modal
	sharesSortColumn: 'name' | 'expirationDate' | 'creationDate' | null = null;
	sharesSortDirection: 'asc' | 'desc' = 'desc';

	// Have a local cache of exports so we know when they have changed. Iterating exportService.Exports doesn't recognize changes
	exports: Export[] = [];

	// Search functionality
	searchText = '';

	// Share status filter properties
	showNotShared = true;
	showShared = true;
	showExpiredOrphanedShares = false;
	showArchived = false;

	// Sorting properties
	sortColumn: 'name' | 'date' | 'type' | null = 'date';
	sortDirection: 'asc' | 'desc' = 'desc';

	// Track export compatibility status for inline warnings
	exportCompatibilityMap: Map<
		number,
		{ isCompatible: boolean; error?: string; isChecking?: boolean }
	> = new Map();

	// Track batch job compatibility status for inline warnings
	batchJobCompatibilityMap: Map<
		string,
		{ isCompatible: boolean; error?: string; isChecking?: boolean }
	> = new Map();

	// Track if initial compatibility check is running
	isRunningInitialCompatibilityCheck = false;

	// Rename modal state
	activeRenameExport: Export | null = null;
	renameName = '';
	renameValidationErrors: string[] = [];
	showRenameValidationErrors = false;
	isRenamingInProgress = false;

	// Delete modal toggle states (deprecated - kept for backwards compatibility)
	deleteModalRemoveExport = true;
	deleteModalRemoveShares = true;

	// Delete export modal state
	deleteExportInProgress = false;
	deleteExportError: string | null = null;

	// Archive export modal state
	archiveExportInProgress = false;
	archiveExportError: string | null = null;
	exportToArchive: Export | null = null;

	// Bulk operations state
	selectedExports: Set<number> = new Set();
	bulkArchiveInProgress = false;
	bulkArchiveError: string | null = null;
	bulkDeleteInProgress = false;
	bulkDeleteError: string | null = null;

	// Cache for orphaned exports (shares whose parent exports no longer exist)
	private cachedOrphanedExports: Export[] = [];
	private orphanedExportIds: Set<number> = new Set();

	// Track batch job progress (job ID -> progress percentage 0-100)
	protected batchJobProgressMap: Map<string, number> = new Map<
		string,
		number
	>();

	// Preview state properties for hover preview feature
	previewExport: Export | null = null;
	showPreview = false;
	previewPosition = { x: 0, y: 0 };
	previewComponentRef: any = null; // Reference to dynamically created component
	previewRequestId = 0; // Track preview requests to handle race conditions
	previewDebounceTimeout: ReturnType<typeof setTimeout> | null = null; // Timeout for debouncing preview display
	@ViewChild('previewContainer', { read: ViewContainerRef })
	previewContainer!: ViewContainerRef;

	ExportToPDF(exportData: Export): void {
		this.pdfService
			.generatePDFFromExport(exportData)
			.then((pdf) => {
				this.pdfService.savePDF(
					pdf,
					`${exportData.name || 'export'}.pdf`,
				);
			})
			.catch((error) => {
				console.error('Error generating PDF file:', error);
				this.notificationService.ShowToast(
					'Virhe PDF-tiedoston luonnissa: ' + error,
					'danger',
				);
			});
	}
	// Local modal - to keep track of the modal that is open locally
	localModalRef: NgbModalRef | undefined = undefined;

	// List of any other child modal than localModalRef that are open in sub-components
	protected openChildModals: NgbModalRef[] = [];

	constructor(
		protected exportService: ExportService,
		private modalService: NgbModal,
		private notificationService: NotificationService,
		private pdfService: PDFService,
		private backendService: BackendService,
		protected sourceMaterialService: SourceMaterialService,
		private migrationService: MigrationService,
		protected batchGenerationService: BatchGenerationService,
		private cdr: ChangeDetectorRef,
		private publicDataService: PublicDataServiceProvider,
		private injector: Injector,
	) {
		this.modalService.activeInstances.subscribe((modals) => {
			this.openChildModals = [];

			modals.forEach((m) => {
				if (m !== this.localModalRef) {
					this.openChildModals.push(m);
				}
			});
		});
	}

	async EditExport(exportData: Export) {
		console.debug('[EditExport] Method called with exportData:', {
			exportName: exportData.name,
			hasGameData: !!exportData.gameData,
			gameDataType: exportData.gameData?.type,
			selectedInterfaceId: exportData.gameData?.selectedInterfaceId,
		});

		let modalRef: NgbModalRef;

		if (exportData.gameData) {
			const gameTypeDefinition =
				this.sourceMaterialService.GetGameTypeSettings(
					exportData.gameData?.type,
				);

			if (
				gameTypeDefinition &&
				gameTypeDefinition.editorComponent !== undefined
			) {
				try {
					// Create a deep copy to avoid modifying the original
					const clonedExportData = structuredClone(exportData);

					// Restore GameData methods and prototypes after deep cloning
					if (clonedExportData.gameData) {
						// Use RendererGameDefinitionService to get the constructor
						const gameDefinitionService =
							new RendererGameDefinitionService();
						const gameDataConstructor =
							await gameDefinitionService.getGameDataConstructor(
								clonedExportData.gameData.type,
							);

						if (gameDataConstructor) {
							GameData.addMethods(
								clonedExportData.gameData,
								gameDataConstructor,
							);

							// Build the GameData types map for nested game data objects
							const availableTypes =
								gameDefinitionService.AvailablePluginTypes;
							const gameDataTypesMap = new Map<
								PluginType,
								new () => GameData
							>();

							for (const pluginType of availableTypes) {
								try {
									const constructor =
										await gameDefinitionService.getGameDataConstructor(
											pluginType,
										);
									if (constructor) {
										gameDataTypesMap.set(
											pluginType,
											constructor,
										);
									}
								} catch (error) {
									console.error(
										`Failed to load constructor for type ${pluginType}:`,
										error,
									);
								}
							}

							// Restore prototypes for all nested game data objects
							clonedExportData.gameData.addPrototypesToAllGameData(
								gameDataTypesMap,
							);
						}
					}

					// Dynamically load the component
					const componentType =
						await gameTypeDefinition.editorComponent();

					modalRef = this.modalService.open(componentType, {
						size: 'xl',
						windowClass: `dark-modal ${gameTypeDefinition.displayMode == 'fullscreen' || (gameTypeDefinition.displayMode == 'both' && gameTypeDefinition.defaultDisplayMode == 'fullscreen') ? ' modal-xxl' : ''}`,
					});

					// CRITICAL: Set export data IMMEDIATELY after modal creation but before Angular initialization
					if (modalRef.componentInstance) {
						// This happens synchronously and before ngOnInit gets called
						console.debug(
							'[EditExport] Setting exportData on component:',
							{
								exportName: clonedExportData.name,
								gameDataType: clonedExportData.gameData?.type,
								selectedInterfaceId:
									clonedExportData.gameData
										?.selectedInterfaceId,
								hasGameData: !!clonedExportData.gameData,
							},
						);
						modalRef.componentInstance.exportData =
							clonedExportData;
					}

					// Store the modal reference for cleanup
					this.localModalRef = modalRef;
				} catch (error) {
					console.error('Error loading component:', error);
					this.notificationService.ShowToast(
						'Komponentin lataaminen epäonnistui',
						'danger',
					);
				}
			}
		} else {
			console.error('Export data is not defined');
			this.notificationService.ShowToast('Pelidataa ei löydy.', 'danger');
			return;
		}

		if (this.localModalRef) {
			this.localModalRef.closed.subscribe(() => {
				this.localModalRef = undefined;
			});
			this.localModalRef.dismissed.subscribe(() => {
				this.localModalRef = undefined;
			});

			// Remove the redundant assignment - modalRef is already set up above
		}
	}

	protected CopyExportToClipboard(exportData: Export): void {
		// Copy the export data to clipboard
		const exportText = JSON.stringify(exportData, null, 2);

		if (navigator.clipboard) {
			navigator.clipboard
				.writeText(exportText)
				.then(() => {
					console.debug('Export data copied to clipboard');
					this.notificationService.ShowToast(
						'Peli kopioitu leikepöydälle',
						'success',
					);
				})
				.catch((err) => {
					console.error('Failed to copy export data:', err);
					this.notificationService.ShowToast(
						'Pelin kopiointi epäonnistui',
						'danger',
					);
				});
		} else {
			// Fallback for older browsers
			const textArea = document.createElement('textarea');
			textArea.value = exportText;
			document.body.appendChild(textArea);
			textArea.select();
			try {
				// Using deprecated API as fallback for older browsers
				const success = document.execCommand('copy');
				if (success) {
					console.debug('Export data copied to clipboard (fallback)');
					this.notificationService.ShowToast(
						'Peli kopioitu leikepöydälle',
						'success',
					);
				}
			} catch (err) {
				console.error('Failed to copy export data (fallback):', err);
				this.notificationService.ShowToast(
					'Pelin kopiointi epäonnistui',
					'danger',
				);
			}
			document.body.removeChild(textArea);
		}
	}

	private resetShareModalState(): void {
		// Simply nullify the active share process and clear date/time fields
		this.activeShareProcess = null;
		this.expirationDatePicker = null;
		this.expirationHour = 23;
		this.expirationMinute = 59;
		this.hasExpiryDate = false;

		// Clear expiration date editing state
		this.isEditingExpirationDate = false;
		this.editExpirationDatePicker = null;
		this.editExpirationHour = 23;
		this.editExpirationMinute = 59;
		this.editHasExpiryDate = false;
		this.shareBeingUpdated = null;

		// Clear shares data
		this.currentExportShares = [];
		this.primaryShare = null;
		this.legacyShares = [];
		this.sharesBeingDeletedInModal.clear();
		this.selectedShareForDeletion = null;
	}

	DeleteShare(shareId: number): void {
		// Remove using this.exportService
		this.exportService.DeleteShare(shareId).subscribe({
			next: () => {
				if (this.activeShareProcess) {
					this.activeShareProcess.isShared = false;
					this.activeShareProcess.sharedUuid = '';
					this.activeShareProcess.isDeleted = true;
				}

				this.notificationService.ShowToast(
					'Pelin julkaisu päätetty',
					'success',
				);
			},
			error: (error: unknown) => {
				this.notificationService.ShowToast(
					'Virhe julkaisun päättämisessä',
					'danger',
				);
				console.error('Error removing share:', error);
			},
		});
	}

	ShareExport(exportData: Export): void {
		// Open the ShareModalComponent
		const modalRef = this.modalService.open(ShareModalComponent, {
			size: 'lg',
			centered: true,
		});

		// Pass the export data to the modal
		if (modalRef.componentInstance) {
			modalRef.componentInstance.exportData = exportData;

			// Subscribe to share created event
			modalRef.componentInstance.shareCreated.subscribe(
				(createdShare: CreatedShare) => {
					// Share was created successfully - no action needed here
					// The modal component handles the notification
					console.log('Share created:', createdShare);
				},
			);
		}
	}

	GetShareIFrameCode(uuid: string): string {
		const link = this.GetShareURL(uuid);
		const iframeCode = `<iframe src="${link}" width="100%" height="100%" frameborder="0" allowfullscreen></iframe>`;
		return iframeCode;
	}

	GetShareURL(uuid: string): string {
		const currentUrl = window.location.href;
		const urlBase = new URL('..', currentUrl).href;
		// Remove trailing slash if present
		const baseUrlWithoutTrailingSlash = urlBase.endsWith('/')
			? urlBase.slice(0, -1)
			: urlBase;

		const dataVersion = environment.appDataVersion;
		return `${baseUrlWithoutTrailingSlash}/viewers/${dataVersion}/#/?uuid=${uuid}`;
	}

	// Update the expirationDate field with combined date and time as UNIX timestamp
	updateExpirationDateTime(): void {
		if (!this.activeShareProcess) return;

		if (this.hasExpiryDate && this.expirationDatePicker) {
			// Create a date from the NgbDateStruct
			const date = new Date(
				this.expirationDatePicker.year,
				this.expirationDatePicker.month - 1, // JavaScript months are 0-indexed
				this.expirationDatePicker.day,
				this.expirationHour || 0,
				this.expirationMinute || 0,
				0, // seconds
			);

			// Convert to UNIX timestamp (seconds, not milliseconds)
			this.activeShareProcess.expirationDate = Math.floor(
				date.getTime() / 1000,
			);
		} else {
			// No expiry date, set to -1
			this.activeShareProcess.expirationDate = -1;
		}
	}

	// Handle checkbox change for expiry date
	onExpiryDateCheckboxChange(): void {
		if (!this.activeShareProcess) return;

		if (this.hasExpiryDate) {
			// Checkbox was just checked, update the expiration date
			this.updateExpirationDateTime();
		} else {
			// Checkbox was unchecked, set to -1
			this.activeShareProcess.expirationDate = -1;
		}
	}

	/* Start sharing to server */
	async shareExperience(): Promise<void> {
		if (!this.activeShareProcess) return;

		// Prevent creating multiple shares - only one share per export is allowed
		if (this.primaryShare !== null) {
			this.notificationService.ShowToast(
				'Julkaisu on jo olemassa. Poista nykyinen julkaisu ensin.',
				'warning',
			);
			return;
		}

		// Update expiration date based on checkbox state before sharing
		if (this.hasExpiryDate) {
			this.updateExpirationDateTime();
		} else {
			this.activeShareProcess.expirationDate = -1;
		}

		if (
			!this.activeShareProcess.exportData ||
			!this.activeShareProcess.exportData.gameData
		) {
			this.notificationService.ShowToast(
				'Virhe: Julkaisusta puuttuu pelidata.',
				'danger',
			);
			console.error(
				'Cannot share experience without exportData or gameData:',
				this.activeShareProcess.exportData,
			);
			return;
		}

		this.activeShareProcess.isSharing = true;

		// Ensure GameData has methods using RendererGameDefinitionService
		const gameDefinitionService = new RendererGameDefinitionService();
		const gameType = this.activeShareProcess?.exportData.gameData?.type;
		const rendererGameDefinition = gameType
			? await gameDefinitionService.getGameDefinition(gameType)
			: undefined;

		if (!rendererGameDefinition) {
			this.notificationService.ShowToast(
				'Virhe: Julkaisun pelityyppiä ei löytynyt',
				'danger',
			);
			console.error(
				'Editor game definition not found for type:',
				this.activeShareProcess.exportData.gameData?.type,
			);
			this.activeShareProcess.isSharing = false;
			return;
		}

		// We need to ensure that the gameData has methods added to it
		GameData.addMethods(
			this.activeShareProcess.exportData.gameData,
			rendererGameDefinition.gameDataType,
		);

		// Build the GameData types map by loading constructors for all available types
		const availableTypes = gameDefinitionService.AvailablePluginTypes;
		const gameDataTypesMap = new Map<PluginType, new () => GameData>();

		for (const pluginType of availableTypes) {
			try {
				const constructor =
					await gameDefinitionService.getGameDataConstructor(
						pluginType,
					);
				if (constructor) {
					gameDataTypesMap.set(pluginType, constructor);
				}
			} catch (error) {
				console.error(
					`Failed to load constructor for type ${pluginType}:`,
					error,
				);
			}
		}

		this.activeShareProcess.exportData.gameData.addPrototypesToAllGameData(
			gameDataTypesMap,
		);

		const usedArticleIds = Array.from(
			new Set(
				this.activeShareProcess.exportData.gameData?.GetArticlesContainedInGameDataObjectsRecursively(
					true,
				) || [],
			),
		).filter((id) => {
			const article = this.sourceMaterialService.GetArticleById(id);

			return (
				article &&
				(article.type == 'image' ||
					article.type == 'audio' ||
					article.type == 'pdf')
			);
		});

		// Use user-given name
		this.activeShareProcess.exportData.name = this.activeShareProcess.name;

		this.exportService
			.ShareExport(this.activeShareProcess, usedArticleIds)
			.subscribe({
				next: (response: CreatedShare) => {
					if (this.activeShareProcess) {
						this.activeShareProcess.isSharing = false;
						this.activeShareProcess.isShared = true;
						this.activeShareProcess.sharedUuid = response.uuid;
						this.activeShareProcess.id = response.id;
						this.activeShareProcess.expirationDate =
							response.expirationDate;

						// Refresh the shares list to show the new share
						this.refreshSharesAfterCreation();

						// Reset the form for next share creation
						this.resetShareFormAfterCreation();

						this.notificationService.ShowToast(
							'Peli julkaistu!',
							'success',
						);
					} else {
						this.notificationService.ShowToast(
							'Virhe pelin julkaisussa',
							'danger',
						);
						console.error('Error: activeShareProcess is null');
					}
				},
				error: (error: unknown) => {
					if (this.activeShareProcess) {
						this.activeShareProcess.isSharing = false;
						this.activeShareProcess.isError = true;

						// Handle different error formats
						let errorMessage = 'Tuntematon virhe';
						// Type assertion for error handling - needed for dynamic error structures
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						const err = error as any;
						if (err?.error?.title) {
							errorMessage = err.error.title;
						} else if (err?.error?.message) {
							errorMessage = err.error.message;
						} else if (err?.message) {
							errorMessage = err.message;
						} else if (err?.status === 401) {
							errorMessage =
								'Valtuutus epäonnistui. Kirjaudu uudelleen.';
						} else if (err?.status) {
							errorMessage = `HTTP ${err.status}: ${err.statusText || 'Virhe'}`;
						}

						this.activeShareProcess.error = errorMessage;
					}

					this.notificationService.ShowToast(
						'Pelin julkaisu epäonnistui',
						'danger',
					);
					console.error('Error sharing experience:', error);
				},
			});
	}

	copySharedLinkToClipboard(link: string): void {
		const linkToCopy = link;
		if (navigator.clipboard) {
			navigator.clipboard
				.writeText(linkToCopy)
				.then(() => {
					this.notificationService.ShowToast(
						`Linkki kopioitu leikepöydälle:\n${linkToCopy}`,
						'success',
					);
				})
				.catch((err) => {
					console.error('Failed to copy link: ', err);
					this.notificationService.ShowToast(
						'Linkin kopiointi epäonnistui',
						'danger',
					);
				});
		} else {
			// Fallback for older browsers
			const textArea = document.createElement('textarea');
			textArea.value = linkToCopy;
			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();
			try {
				// Using deprecated API as fallback for older browsers
				document.execCommand('copy');
				this.notificationService.ShowToast(
					`Linkki kopioitu leikepöydälle:\n${linkToCopy}`,
					'success',
				);
			} catch (err) {
				console.error('Failed to copy link (fallback): ', err);
				this.notificationService.ShowToast(
					'Linkin kopiointi epäonnistui',
					'danger',
				);
			}
			document.body.removeChild(textArea);
		}
	}

	openSharedLinkInNewWindow(link: string): void {
		window.open(link, '_blank');
	}

	/**
	 * Show confirmation dialog before deleting an export
	 * @param exportData Export to delete
	 * @param modalTemplate Template reference for the confirmation modal
	 */
	confirmDeleteExport(
		exportData: Export,
		modalTemplate: TemplateRef<unknown>,
	): void {
		// Store the export to be deleted so the modal can display its name
		this.exportToDelete = exportData;

		// Reset delete modal state
		this.deleteExportInProgress = false;
		this.deleteExportError = null;

		// Set toggle states based on export context (for backwards compatibility)
		const isOrphaned = this.isOrphanedExport(exportData.id);
		const shareCount = this.getShareCount(exportData.id);

		// For orphaned exports: can only delete shares (export doesn't exist)
		// For normal exports: can delete export and optionally shares
		this.deleteModalRemoveExport = !isOrphaned;
		this.deleteModalRemoveShares = shareCount > 0;

		this.modalService.open(modalTemplate, {
			centered: true,
			backdrop: 'static', // Prevent closing by clicking outside while operation is in progress
			keyboard: false, // Prevent closing with ESC key while operation is in progress
		});
	}

	/**
	 * Execute the delete operation: first delete all shares, then delete the export
	 * @param modal Modal reference to close upon success
	 */
	executeDeleteExport(modal: any): void {
		if (!this.exportToDelete || !this.exportToDelete.id) {
			return;
		}

		const exportId = this.exportToDelete.id;
		const exportName = this.exportToDelete.name;

		// Set loading state
		this.deleteExportInProgress = true;
		this.deleteExportError = null;

		// Mark experience as being deleted (for UI spinner in table)
		this.experiencesBeingDeleted.add(exportId);

		// Get all shares for this export
		const shares =
			this.exportService.GetSharedExperiencesForExport(exportId);

		// Step 1: Delete all shares first (if any exist)
		const deleteSharesObservable =
			shares.length > 0
				? forkJoin(
						shares.map((share) =>
							this.exportService.DeleteShare(share.id),
						),
					).pipe(map(() => undefined as void))
				: of(undefined as void); // If no shares, return completed observable

		deleteSharesObservable
			.pipe(
				// Step 2: After shares are deleted, delete the export
				switchMap(() => this.backendService.DeleteExperience(exportId)),
				catchError((error: unknown) => {
					console.error('Error during delete operation:', error);
					this.deleteExportError =
						'Virhe poistossa. Yritä uudelleen tai ota yhteyttä tukeen.';
					this.deleteExportInProgress = false;
					this.experiencesBeingDeleted.delete(exportId);
					return throwError(() => error);
				}),
			)
			.subscribe({
				next: () => {
					// Success - remove from local state
					this.exportService.DeleteExportFromLocal(exportId);
					this.exportService.RemoveSharesForExport(exportId);
					this.experiencesBeingDeleted.delete(exportId);

					// Show success message
					const shareCount = shares.length;
					const message =
						shareCount > 0
							? `Peli "${exportName}" ja ${shareCount} julkaisua poistettu.`
							: `Peli "${exportName}" poistettu.`;

					this.notificationService.ShowToast(message, 'success');

					// Close modal and reset state
					modal.dismiss('Success');
					this.exportToDelete = null;
					this.deleteExportInProgress = false;
					this.deleteExportError = null;
				},
				error: () => {
					// Error state is already set in catchError
				},
			});
	}

	/**
	 * Check if an export is selected
	 */
	isExportSelected(exportId: number): boolean {
		return this.selectedExports.has(exportId);
	}

	/**
	 * Toggle export selection
	 */
	toggleExportSelection(exportId: number): void {
		if (this.selectedExports.has(exportId)) {
			this.selectedExports.delete(exportId);
		} else {
			this.selectedExports.add(exportId);
		}
	}

	/**
	 * Select all visible (filtered) exports
	 */
	selectAllExports(): void {
		const visibleExports = this.filteredExports;
		if (visibleExports.length === 0) return;

		// If all visible exports are already selected, deselect all
		const allSelected = visibleExports.every((exp) =>
			this.selectedExports.has(exp.id),
		);

		if (allSelected) {
			// Deselect all visible exports
			visibleExports.forEach((exp) => {
				this.selectedExports.delete(exp.id);
			});
		} else {
			// Select all visible exports
			visibleExports.forEach((exp) => {
				if (exp.id && exp.id !== -1) {
					this.selectedExports.add(exp.id);
				}
			});
		}
	}

	/**
	 * Clear all selections
	 */
	clearSelection(): void {
		this.selectedExports.clear();
	}

	DeleteExport(exportData: Export): void {
		if (!exportData.id || exportData.id === -1) {
			this.notificationService.ShowToast(
				'Virhe: pelin tunniste puuttuu.',
				'danger',
			);
			console.error('Cannot delete export without ID:', exportData);
			return;
		}

		const isOrphaned = this.isOrphanedExport(exportData.id);

		// Check if both toggles are unchecked (nothing to do)
		if (!this.deleteModalRemoveExport && !this.deleteModalRemoveShares) {
			this.notificationService.ShowToast(
				'Ei mitään poistettavaa',
				'warning',
			);
			return;
		}

		// Mark this experience as being deleted
		this.experiencesBeingDeleted.add(exportData.id);

		// Handle orphaned export (only delete shares)
		if (isOrphaned) {
			const shares = this.exportService.GetSharedExperiencesForExport(
				exportData.id,
			);
			if (shares.length === 0) {
				this.experiencesBeingDeleted.delete(exportData.id);
				this.notificationService.ShowToast(
					'Ei jaettuja kokemuksia poistettavaksi',
					'warning',
				);
				return;
			}

			// Delete all shares using forkJoin for parallel deletion
			const deleteObservables = shares.map((share) =>
				this.exportService.DeleteShare(share.id),
			);

			forkJoin(deleteObservables).subscribe({
				next: () => {
					this.experiencesBeingDeleted.delete(exportData.id);
					this.notificationService.ShowToast(
						`Kaikki jaetut kokemukset poistettu (${shares.length} kpl)`,
						'success',
					);
				},
				error: (error: unknown) => {
					this.experiencesBeingDeleted.delete(exportData.id);
					console.error('Error deleting shares:', error);
					this.notificationService.ShowToast(
						'Virhe jaettujen kokemusten poistossa',
						'danger',
					);
				},
			});
			return;
		}

		// Handle normal export based on toggle states
		if (this.deleteModalRemoveExport && !this.deleteModalRemoveShares) {
			// Delete only the export (not shares)
			this.backendService.DeleteExperience(exportData.id).subscribe({
				next: () => {
					if (exportData.id) {
						this.experiencesBeingDeleted.delete(exportData.id);
						this.exportService.DeleteExportFromLocal(exportData.id);
					}
					this.notificationService.ShowToast(
						`Peli "${exportData.name}" poistettu.`,
						'success',
					);
				},
				error: (error: unknown) => {
					if (exportData.id)
						this.experiencesBeingDeleted.delete(exportData.id);
					console.error('Error deleting experience:', error);
					this.notificationService.ShowToast(
						`Virhe pelin poistossa "${exportData.name}".`,
						'danger',
					);
				},
			});
		} else if (
			!this.deleteModalRemoveExport &&
			this.deleteModalRemoveShares
		) {
			// Delete only shares (keep the export)
			const shares = this.exportService.GetSharedExperiencesForExport(
				exportData.id,
			);
			if (shares.length === 0) {
				this.experiencesBeingDeleted.delete(exportData.id);
				this.notificationService.ShowToast(
					'Ei jaettuja kokemuksia poistettavaksi',
					'warning',
				);
				return;
			}

			const deleteObservables = shares.map((share) =>
				this.exportService.DeleteShare(share.id),
			);

			forkJoin(deleteObservables).subscribe({
				next: () => {
					this.experiencesBeingDeleted.delete(exportData.id);
					this.notificationService.ShowToast(
						`Kaikki jaetut kokemukset poistettu (${shares.length} kpl)`,
						'success',
					);
				},
				error: (error: unknown) => {
					this.experiencesBeingDeleted.delete(exportData.id);
					console.error('Error deleting shares:', error);
					this.notificationService.ShowToast(
						'Virhe jaettujen kokemusten poistossa',
						'danger',
					);
				},
			});
		} else {
			// Delete both export and shares
			// When export is deleted, shares will also be cascade-deleted on backend
			this.backendService.DeleteExperience(exportData.id).subscribe({
				next: () => {
					if (exportData.id) {
						this.experiencesBeingDeleted.delete(exportData.id);
						// Remove export from local state (emits OnExportsChanged)
						this.exportService.DeleteExportFromLocal(exportData.id);
						// Remove cascade-deleted shares from local state
						this.exportService.RemoveSharesForExport(exportData.id);
					}
					this.notificationService.ShowToast(
						`Peli "${exportData.name}" ja kaikki julkaisut poistettu.`,
						'success',
					);
				},
				error: (error: unknown) => {
					if (exportData.id)
						this.experiencesBeingDeleted.delete(exportData.id);
					console.error('Error deleting experience:', error);
					this.notificationService.ShowToast(
						`Virhe pelin poistossa "${exportData.name}".`,
						'danger',
					);
				},
			});
		}
	}

	async PlayExport(exportData: Export): Promise<void> {
		console.debug('=== PlayExport DEBUG START ===');
		console.debug('Export Data:', {
			id: exportData.id,
			name: exportData.name,
			date: exportData.date,
			creator: exportData.creator,
			version: exportData.version,
			hasGameData: !!exportData.gameData,
			gameDataType: exportData.gameData?.type,
			gameDataKeys: exportData.gameData
				? Object.keys(exportData.gameData)
				: [],
		});

		let modalRef: NgbModalRef;

		if (exportData.gameData) {
			console.debug('Game Data Details:', {
				type: exportData.gameData.type,
				selectedInterfaceId: exportData.gameData.selectedInterfaceId,
				colorPalette: exportData.gameData.colorPalette,
				gameDataConstructor: exportData.gameData.constructor.name,
				gameDataString:
					JSON.stringify(exportData.gameData).substring(0, 200) +
					'...',
			});

			// Validate GameData object has required prototype methods for rendering
			console.debug('Validating GameData prototype methods...', {
				hasGetUsedArticles:
					typeof exportData.gameData.GetUsedArticles === 'function',
				hasGetContainedLocalGameDataObjects:
					typeof exportData.gameData
						.GetContainedLocalGameDataObjects === 'function',
				constructorName: exportData.gameData.constructor.name,
				hasType: exportData.gameData.type !== undefined,
			});

			// Check if prototype restoration is needed (constructor.name is 'Object' indicates plain object)
			if (
				exportData.gameData.constructor.name === 'Object' ||
				typeof exportData.gameData.GetUsedArticles !== 'function' ||
				typeof exportData.gameData.GetContainedLocalGameDataObjects !==
					'function'
			) {
				console.debug(
					'GameData missing prototype methods - restoring...',
				);

				try {
					// Use RendererGameDefinitionService to get the constructor
					const gameDefinitionService =
						new RendererGameDefinitionService();
					const gameDataConstructor =
						await gameDefinitionService.getGameDataConstructor(
							exportData.gameData.type,
						);

					if (gameDataConstructor) {
						console.debug(
							'Restoring GameData prototype methods...',
						);
						GameData.addMethods(
							exportData.gameData,
							gameDataConstructor,
						);

						// Build the GameData types map for nested game data objects
						const availableTypes =
							gameDefinitionService.AvailablePluginTypes;
						const gameDataTypesMap = new Map<
							PluginType,
							new () => GameData
						>();

						for (const pluginType of availableTypes) {
							try {
								const constructor =
									await gameDefinitionService.getGameDataConstructor(
										pluginType,
									);
								if (constructor) {
									gameDataTypesMap.set(
										pluginType,
										constructor,
									);
								}
							} catch (error) {
								console.debug(
									`Could not load constructor for plugin type ${pluginType}:`,
									error,
								);
							}
						}

						// Restore methods for ALL GameData objects (including nested ones)
						// This uses a two-pass approach that doesn't require methods to exist during collection
						exportData.gameData.addPrototypesToAllGameData(
							gameDataTypesMap,
						);
						console.debug(
							'All GameData prototypes restored (including nested objects)',
						);
					} else {
						console.warn(
							'Could not find GameData constructor for type:',
							exportData.gameData.type,
						);
						this.notificationService.ShowToast(
							'Pelin prototyypin palauttaminen epäonnistui, peli saattaa toimia virheellisesti',
							'warning',
						);
					}
				} catch (error) {
					console.error(
						'Error restoring GameData prototype methods:',
						error,
					);
					this.notificationService.ShowToast(
						'Pelin prototyypin palauttaminen epäonnistui',
						'warning',
					);
				}
			}

			// Validate game data using game-specific validation if available
			console.debug('Validating game data...');
			try {
				const gameDefinitionService =
					new RendererGameDefinitionService();
				const validationResult =
					await gameDefinitionService.validateGameDataForImport(
						exportData.gameData.type,
						exportData.gameData,
					);

				if (!validationResult.isValid) {
					console.warn(
						'Game data validation failed:',
						validationResult.errors,
					);

					// Show specific validation error(s) in danger notification
					const errorMessage =
						validationResult.errors.length > 0
							? `Validointivirhe: ${validationResult.errors.join(', ')}`
							: 'Sisällössä on validointivirheitä. Esikatselu ei ole mahdollinen.';

					this.notificationService.ShowToast(errorMessage, 'danger');
					return; // Exit early
				} else {
					console.debug('Game data validation passed');
				}
			} catch (error) {
				console.error('Error during game data validation:', error);
				this.notificationService.ShowToast(
					'Pelin tietojen validointi epäonnistui, mutta yritetään jatkaa',
					'warning',
				);
			}

			const gameTypeDefinition =
				this.sourceMaterialService.GetGameTypeSettings(
					exportData.gameData?.type,
				);

			console.debug('Game Type Definition:', {
				found: !!gameTypeDefinition,
				hasRendererComponent: !!gameTypeDefinition?.rendererComponent,
				displayMode: gameTypeDefinition?.displayMode,
				defaultDisplayMode: gameTypeDefinition?.defaultDisplayMode,
				title: gameTypeDefinition?.title,
			});

			if (
				gameTypeDefinition &&
				gameTypeDefinition.rendererComponent !== undefined
			) {
				try {
					console.debug('Loading renderer component...');
					// Dynamically load the component
					const componentType =
						await gameTypeDefinition.rendererComponent();

					console.debug('Component loaded successfully:', {
						componentName: componentType?.name,
						componentType: typeof componentType,
					});

					const windowClass = `dark-modal ${gameTypeDefinition.displayMode == 'fullscreen' || (gameTypeDefinition.displayMode == 'both' && gameTypeDefinition.defaultDisplayMode == 'fullscreen') ? 'modal-xxl' : ''}`;
					console.debug(
						'Opening modal with windowClass:',
						windowClass,
					);

					modalRef = this.modalService.open(componentType, {
						size: 'xl',
						windowClass: windowClass,
					});

					console.debug('Modal opened:', {
						hasModalRef: !!modalRef,
						hasComponentInstance: !!modalRef?.componentInstance,
					});

					if (modalRef && modalRef.componentInstance) {
						console.debug(
							'Setting exportData on component instance...',
						);
						// Give data for the component to render
						modalRef.componentInstance.exportData = exportData;
						console.debug('ExportData assigned successfully');
					} else {
						console.error(
							'Modal or component instance not available',
						);
						this.notificationService.ShowToast(
							'Pelin lataaminen epäonnistui: Modal ei aukea',
							'danger',
						);
					}
				} catch (error) {
					console.error('Error loading component:', error);
					console.debug('Component loading error details:', {
						errorMessage:
							error instanceof Error
								? error.message
								: 'Unknown error',
						errorStack:
							error instanceof Error
								? error.stack
								: 'No stack trace',
						gameType: exportData.gameData?.type,
					});
					this.notificationService.ShowToast(
						'Komponentin lataaminen epäonnistui',
						'danger',
					);
				}
			} else {
				console.error(
					'Game type definition or renderer component not found',
				);
				console.debug('Missing renderer details:', {
					gameType: exportData.gameData?.type,
					hasGameTypeDefinition: !!gameTypeDefinition,
					hasRendererComponent:
						!!gameTypeDefinition?.rendererComponent,
				});
				this.notificationService.ShowToast(
					'Pelin renderöintikomponentti ei löydy',
					'danger',
				);
			}
		} else {
			console.error('Export data gameData is not defined');
			console.debug('Export data without gameData:', exportData);
			this.notificationService.ShowToast('Pelidataa ei löydy.', 'danger');
			return;
		}
		console.debug('=== PlayExport DEBUG END ===');
	}

	/**
	 * Play an orphaned export by previewing its first (most recent) share
	 */
	PlayOrphanedExport(exportId: number): void {
		// Get the shares for this orphaned export
		const shares =
			this.exportService.GetSharedExperiencesForExport(exportId);
		if (shares.length === 0) {
			this.notificationService.ShowToast('Jakoja ei löytynyt', 'warning');
			return;
		}

		// Sort by creation date and get the most recent one
		const sortedShares = [...shares].sort(
			(a, b) => b.creationDate - a.creationDate,
		);
		const firstShare = sortedShares[0];

		// Use the existing playShareFromModal method
		this.playShareFromModal(firstShare);
	}

	closeModal(): void {
		// Close all except current modal
		this.openChildModals.forEach((modal) => {
			modal.close();
		});
	}

	HasOtherModalsOpen(): boolean {
		return this.openChildModals.some((m) => m !== this.localModalRef);
	}

	/**
	 * Opens the confirmation modal for deleting all experiences
	 */
	openDeleteAllModal(): void {
		this.deleteAllModalRef = this.modalService.open(
			this.deleteAllModalTemplate,
			{
				centered: true,
				backdrop: 'static',
				keyboard: false,
			},
		);

		this.deleteAllModalRef.result.then(
			() => {
				// Modal closed (not used here as we handle deletion in deleteAllExperiences)
				this.deleteAllModalRef = undefined;
			},
			() => {
				// Modal dismissed
				this.deleteAllModalRef = undefined;
			},
		);
	}

	/**
	 * Open confirmation modal for archiving/unarchiving an export
	 * @param exportData Export to archive/unarchive
	 * @param modalTemplate Template reference for the confirmation modal
	 */
	confirmArchiveExport(
		exportData: Export,
		modalTemplate: TemplateRef<unknown>,
	): void {
		// Store the export to be archived so the modal can display its name
		this.exportToArchive = exportData;

		// Reset archive modal state
		this.archiveExportInProgress = false;
		this.archiveExportError = null;

		this.modalService.open(modalTemplate, {
			centered: true,
			backdrop: 'static',
			keyboard: false,
		});
	}

	/**
	 * Execute the archive/unarchive operation
	 * @param modal Modal reference to close upon success
	 */
	executeArchiveExport(modal: any): void {
		if (!this.exportToArchive || !this.exportToArchive.id) {
			return;
		}

		const exportId = this.exportToArchive.id;
		const exportName = this.exportToArchive.name;
		const isCurrentlyArchived =
			this.exportService.isExportArchived(exportId);
		const newArchivedState = !isCurrentlyArchived;

		// Set loading state
		this.archiveExportInProgress = true;
		this.archiveExportError = null;

		// Update the export (archived state will be passed to UpdateExport)
		const updatedExport = { ...this.exportToArchive };

		// Call UpdateExport to save changes to backend, passing archived state
		this.exportService
			.UpdateExport(updatedExport, newArchivedState)
			.pipe(
				catchError((error: unknown) => {
					console.error('Error during archive operation:', error);
					this.archiveExportError =
						'Virhe arkistoinnissa. Yritä uudelleen tai ota yhteyttä tukeen.';
					this.archiveExportInProgress = false;
					return throwError(() => error);
				}),
			)
			.subscribe({
				next: () => {
					// Success - show message
					const message = newArchivedState
						? `Peli "${exportName}" arkistoitu.`
						: `Peli "${exportName}" palautettu päänäkymään.`;

					this.notificationService.ShowToast(message, 'success');

					// Close modal and reset state
					modal.dismiss('Success');
					this.exportToArchive = null;
					this.archiveExportInProgress = false;
					this.archiveExportError = null;
				},
				error: () => {
					// Error state is already set in catchError
				},
			});
	}

	/**
	 * Open confirmation modal for bulk archiving selected exports
	 * @param modalTemplate Template reference for the confirmation modal
	 */
	confirmBulkArchive(modalTemplate: TemplateRef<unknown>): void {
		if (this.selectedExports.size === 0) {
			return;
		}

		// Reset bulk archive modal state
		this.bulkArchiveInProgress = false;
		this.bulkArchiveError = null;

		this.modalService.open(modalTemplate, {
			centered: true,
			backdrop: 'static',
			keyboard: false,
		});
	}

	/**
	 * Execute bulk archive operation
	 * @param modal Modal reference to close upon success
	 */
	executeBulkArchive(modal: any): void {
		if (this.selectedExports.size === 0) {
			return;
		}

		// Set loading state
		this.bulkArchiveInProgress = true;
		this.bulkArchiveError = null;

		// Get exports to archive
		const exportsToArchive = this.exports.filter((exp) =>
			this.selectedExports.has(exp.id),
		);

		if (exportsToArchive.length === 0) {
			this.bulkArchiveError = 'Valittuja pelejä ei löytynyt.';
			this.bulkArchiveInProgress = false;
			return;
		}

		// Create update observables for each export
		const updateObservables = exportsToArchive.map((exp) => {
			const updatedExport = { ...exp };
			return this.exportService.UpdateExport(updatedExport, true);
		});

		// Execute all updates in parallel
		forkJoin(updateObservables)
			.pipe(
				catchError((error: unknown) => {
					console.error('Error during bulk archive:', error);
					this.bulkArchiveError =
						'Virhe arkistoinnissa. Yritä uudelleen tai ota yhteyttä tukeen.';
					this.bulkArchiveInProgress = false;
					return throwError(() => error);
				}),
			)
			.subscribe({
				next: () => {
					// Success - clear selection and show message
					const count = exportsToArchive.length;
					this.notificationService.ShowToast(
						`${count} peliä arkistoitu onnistuneesti.`,
						'success',
					);

					// Clear selection
					this.clearSelection();

					// Close modal and reset state
					modal.dismiss('Success');
					this.bulkArchiveInProgress = false;
					this.bulkArchiveError = null;
				},
				error: () => {
					// Error state is already set in catchError
				},
			});
	}

	/**
	 * Open confirmation modal for bulk deleting selected exports
	 * @param modalTemplate Template reference for the confirmation modal
	 */
	confirmBulkDelete(modalTemplate: TemplateRef<unknown>): void {
		if (this.selectedExports.size === 0) {
			return;
		}

		// Reset bulk delete modal state
		this.bulkDeleteInProgress = false;
		this.bulkDeleteError = null;

		this.modalService.open(modalTemplate, {
			centered: true,
			backdrop: 'static',
			keyboard: false,
		});
	}

	/**
	 * Execute bulk delete operation
	 * @param modal Modal reference to close upon success
	 */
	executeBulkDelete(modal: any): void {
		if (this.selectedExports.size === 0) {
			return;
		}

		// Set loading state
		this.bulkDeleteInProgress = true;
		this.bulkDeleteError = null;

		// Get exports to delete
		const exportsToDelete = this.exports.filter((exp) =>
			this.selectedExports.has(exp.id),
		);

		if (exportsToDelete.length === 0) {
			this.bulkDeleteError = 'Valittuja pelejä ei löytynyt.';
			this.bulkDeleteInProgress = false;
			return;
		}

		// Mark all as being deleted (for UI spinners)
		exportsToDelete.forEach((exp) =>
			this.experiencesBeingDeleted.add(exp.id),
		);

		// For each export: delete shares first, then the export
		const deleteOperations = exportsToDelete.map((exp) => {
			const shares = this.exportService.GetSharedExperiencesForExport(
				exp.id,
			);

			// Delete shares first (if any), then the export
			const deleteSharesObs =
				shares.length > 0
					? forkJoin(
							shares.map((share) =>
								this.exportService.DeleteShare(share.id),
							),
						).pipe(map(() => undefined as void))
					: of(undefined as void);

			return deleteSharesObs.pipe(
				switchMap(() => this.backendService.DeleteExperience(exp.id)),
				map(() => ({ exportId: exp.id, exportName: exp.name })),
			);
		});

		// Execute all delete operations in parallel
		forkJoin(deleteOperations)
			.pipe(
				catchError((error: unknown) => {
					console.error('Error during bulk delete:', error);
					this.bulkDeleteError =
						'Virhe poistossa. Yritä uudelleen tai ota yhteyttä tukeen.';
					this.bulkDeleteInProgress = false;
					// Clear deletion markers
					exportsToDelete.forEach((exp) =>
						this.experiencesBeingDeleted.delete(exp.id),
					);
					return throwError(() => error);
				}),
			)
			.subscribe({
				next: (results) => {
					// Success - remove from local state
					results.forEach((result) => {
						this.exportService.DeleteExportFromLocal(
							result.exportId,
						);
						this.exportService.RemoveSharesForExport(
							result.exportId,
						);
						this.experiencesBeingDeleted.delete(result.exportId);
					});

					// Show success message
					const count = results.length;
					this.notificationService.ShowToast(
						`${count} peliä poistettu onnistuneesti.`,
						'success',
					);

					// Clear selection
					this.clearSelection();

					// Close modal and reset state
					modal.dismiss('Success');
					this.bulkDeleteInProgress = false;
					this.bulkDeleteError = null;
				},
				error: () => {
					// Error state is already set in catchError
				},
			});
	}

	/**
	 * Deletes all experiences after confirmation
	 */
	// deleteAllExperiences(): void {
	// 	// Close the modal first
	// 	if (this.deleteAllModalRef) {
	// 		this.deleteAllModalRef.close();
	// 	}

	// 	// Mark all experiences as being deleted
	// 	this.exportService.Exports.forEach((exp) => {
	// 		if (exp.id !== undefined && exp.id !== -1) {
	// 			this.experiencesBeingDeleted.add(exp.id);
	// 		}
	// 	});

	// 	// Subscribe to the DeleteAllExperiences method in exportService
	// 	this.exportService.DeleteAllExperiences();

	// 	// Show notification
	// 	this.notificationService.ShowToast(
	// 		'Kaikki pelit poistetaan...',
	// 		'warning',
	// 	);
	// }

	/**
	 * Initialize the component and subscribe to exports changes
	 */
	ngOnInit(): void {
		this.exports = this.exportService.Exports;

		// Subscribe to exports changes
		const exportsChangedSub = this.exportService.OnExportsChanged.subscribe(
			{
				next: (exports: Export[]) => {
					console.log(
						'[OnExportsChanged] Received exports:',
						exports.length,
					);
					this.exports = exports;
					// Rebuild orphaned exports cache when exports change
					this.computeOrphanedExports();
					// Run compatibility check when exports change
					this.checkExportCompatibilityOnLoad();
				},
				error: (error: unknown) => {
					const err = error as Error;
					this.errorMessage =
						'Virhe pelejä ladattaessa: ' + (err.message || error);
					console.error('Error in exports subscription:', error);
				},
			},
		);

		this.subscriptions.push(exportsChangedSub);

		// Subscribe to shared experiences changes
		const sharedExperiencesChangedSub =
			this.exportService.OnSharedExperiencesChanged.subscribe({
				next: () => {
					// Rebuild orphaned exports cache when shares change
					this.computeOrphanedExports();
				},
				error: (error: unknown) => {
					console.error(
						'Error in shared experiences subscription:',
						error,
					);
				},
			});

		this.subscriptions.push(sharedExperiencesChangedSub);

		// Compute initial orphaned exports
		this.computeOrphanedExports();

		// Subscribe to batch generation events to update UI when job status changes
		const batchJobAddedSub =
			this.batchGenerationService.onJobAdded.subscribe(() => {
				// UI will update automatically via getters
			});

		const batchJobCompletedSub =
			this.batchGenerationService.onJobCompleted.subscribe(() => {
				// UI will update automatically via getters
			});

		const batchJobFailedSub =
			this.batchGenerationService.onJobFailed.subscribe(() => {
				// UI will update automatically via getters
			});

		// Subscribe to batch job progress updates to show progress in UI
		const batchJobProgressSub =
			this.batchGenerationService.onStepProgress.subscribe((progress) => {
				// Calculate progress percentage and update map
				// Progress comes as: { jobId, stepId, completed, total, currentItem? }
				const progressPercent = Math.round(
					(progress.completed / progress.total) * 100,
				);
				this.batchJobProgressMap.set(progress.jobId, progressPercent);
			});

		this.subscriptions.push(
			batchJobAddedSub,
			batchJobCompletedSub,
			batchJobFailedSub,
			batchJobProgressSub,
		);

		// Run initial compatibility check
		if (this.exports.length > 0) {
			this.checkExportCompatibilityOnLoad();
		}
	}

	/**
	 * Clean up subscriptions when the component is destroyed
	 */
	ngOnDestroy(): void {
		// Unsubscribe from all subscriptions
		this.subscriptions.forEach((sub) => sub.unsubscribe());
		this.subscriptions = [];
	}

	/**
	 * Called when the showArchived toggle changes.
	 * Triggers loading of archived exports when user first enters the archived view.
	 */
	onShowArchivedChange(): void {
		if (this.showArchived) {
			// User has toggled to show archived exports
			// The service will handle the duplicate-check internally
			this.exportService.loadArchivedExports();
		}
	}

	/**
	 * Check compatibility of exports on page load for inline warnings
	 */
	private checkExportCompatibilityOnLoad(): void {
		if (
			(this.exports.length === 0 && this.unsavedBatchJobs.length === 0) ||
			this.isRunningInitialCompatibilityCheck
		) {
			return;
		}

		this.isRunningInitialCompatibilityCheck = true;

		// Check each export individually and update compatibility map
		this.exports.forEach((exp) => {
			if (exp.id && exp.id !== -1) {
				// Mark as checking
				this.exportCompatibilityMap.set(exp.id, {
					isCompatible: true, // Assume compatible until proven otherwise
					isChecking: true,
				});

				// Check compatibility
				this.migrationService
					.checkExportCompatibilityWithError(exp)
					.subscribe({
						next: (result) => {
							if (exp.id) {
								this.exportCompatibilityMap.set(exp.id, {
									isCompatible: result.isCompatible,
									error: result.error,
									isChecking: false,
								});
							}
						},
						error: (error) => {
							console.error(
								`[ExportsComponent] ERROR: Compatibility check failed for export ${exp.id}:`,
								error,
							);
							if (exp.id) {
								this.exportCompatibilityMap.set(exp.id, {
									isCompatible: false,
									error: 'Compatibility check failed',
									isChecking: false,
								});
							}
						},
					});
			}
		});

		// Check each batch job individually and update compatibility map
		this.unsavedBatchJobs.forEach((job) => {
			if (job.id && job.status === 'completed' && job.result) {
				// Mark as checking
				this.batchJobCompatibilityMap.set(job.id, {
					isCompatible: true, // Assume compatible until proven otherwise
					isChecking: true,
				});

				// Check compatibility of the batch job result (Export)
				this.migrationService
					.checkExportCompatibilityWithError(job.result)
					.subscribe({
						next: (result) => {
							this.batchJobCompatibilityMap.set(job.id, {
								isCompatible: result.isCompatible,
								error: result.error,
								isChecking: false,
							});
						},
						error: (error) => {
							console.error(
								`[ExportsComponent] ERROR: Compatibility check failed for batch job ${job.id}:`,
								error,
							);
							this.batchJobCompatibilityMap.set(job.id, {
								isCompatible: false,
								error: 'Compatibility check failed',
								isChecking: false,
							});
						},
					});
			}
		});

		// Set flag to false after a short delay (all async checks initiated)
		setTimeout(() => {
			this.isRunningInitialCompatibilityCheck = false;
		}, 100);
	}

	/**
	 * Get compatibility status for an export
	 */
	getExportCompatibilityStatus(exportId: number | undefined): {
		isCompatible: boolean;
		error?: string;
		isChecking?: boolean;
	} {
		if (!exportId || exportId === -1) {
			return { isCompatible: true };
		}

		return (
			this.exportCompatibilityMap.get(exportId) || { isCompatible: true }
		);
	}

	/**
	 * Get compatibility status for a batch job
	 */
	getBatchJobCompatibilityStatus(jobId: string | undefined): {
		isCompatible: boolean;
		error?: string;
		isChecking?: boolean;
	} {
		if (!jobId) {
			return { isCompatible: true };
		}

		return (
			this.batchJobCompatibilityMap.get(jobId) || { isCompatible: true }
		);
	}

	/**
	 * Get the number of shares for an export
	 */
	getShareCount(exportId: number | undefined): number {
		// Allow 0 as a valid exportId (for pseudo exports with experienceId: 0)
		if (exportId === undefined || exportId === null || exportId === -1) {
			return 0;
		}

		const shares =
			this.exportService.GetSharedExperiencesForExport(exportId);

		return shares.length;
	}

	/**
	 * Get the primary (most recent) share for an export
	 * Returns null if no shares exist
	 */
	getPrimaryShare(exportId: number): CreatedShare | null {
		const shares =
			this.exportService.GetSharedExperiencesForExport(exportId);
		if (shares.length === 0) return null;

		// Return most recent share (newest creationDate)
		return shares.sort((a, b) => b.creationDate - a.creationDate)[0];
	}

	/**
	 * Get legacy shares for an export (all shares except the primary/most recent)
	 * Returns empty array if there are 0 or 1 shares
	 */
	getLegacyShares(exportId: number): CreatedShare[] {
		const shares =
			this.exportService.GetSharedExperiencesForExport(exportId);
		if (shares.length <= 1) return [];

		const sorted = shares.sort((a, b) => b.creationDate - a.creationDate);
		return sorted.slice(1); // All except the most recent
	}

	/**
	 * Start editing the expiration date of the primary share
	 */
	startEditingExpirationDate(): void {
		if (!this.primaryShare) return;

		this.isEditingExpirationDate = true;

		// Populate edit fields with current expiration date
		if (
			this.primaryShare.expirationDate &&
			this.primaryShare.expirationDate !== -1
		) {
			this.editHasExpiryDate = true;
			const expirationDate = new Date(
				this.primaryShare.expirationDate * 1000,
			);

			this.editExpirationDatePicker = {
				year: expirationDate.getFullYear(),
				month: expirationDate.getMonth() + 1,
				day: expirationDate.getDate(),
			};
			this.editExpirationHour = expirationDate.getHours();
			this.editExpirationMinute = expirationDate.getMinutes();
		} else {
			// No expiration date set
			this.editHasExpiryDate = false;
			const tomorrow = new Date();
			tomorrow.setDate(tomorrow.getDate() + 1);

			this.editExpirationDatePicker = {
				year: tomorrow.getFullYear(),
				month: tomorrow.getMonth() + 1,
				day: tomorrow.getDate(),
			};
			this.editExpirationHour = 23;
			this.editExpirationMinute = 59;
		}
	}

	/**
	 * Cancel editing the expiration date
	 */
	cancelEditingExpirationDate(): void {
		this.isEditingExpirationDate = false;
		this.shareBeingUpdated = null;
		this.editExpirationDatePicker = null;
		this.editExpirationHour = 23;
		this.editExpirationMinute = 59;
		this.editHasExpiryDate = false;
	}

	/**
	 * Update the edit expiration date/time based on edit fields
	 */
	updateEditExpirationDateTime(): number {
		if (this.editHasExpiryDate && this.editExpirationDatePicker) {
			const date = new Date(
				this.editExpirationDatePicker.year,
				this.editExpirationDatePicker.month - 1,
				this.editExpirationDatePicker.day,
				this.editExpirationHour || 0,
				this.editExpirationMinute || 0,
				0,
			);
			return Math.floor(date.getTime() / 1000);
		}
		return -1; // No expiration
	}

	/**
	 * Save the edited expiration date to the server
	 */
	saveExpirationDate(): void {
		if (!this.primaryShare || this.shareBeingUpdated !== null) return;

		const newExpirationDate = this.updateEditExpirationDateTime();

		// Set loading state
		this.shareBeingUpdated = this.primaryShare.id;

		// Call backend to update the share
		this.backendService
			.UpdateShare(this.primaryShare.id, true, newExpirationDate)
			.subscribe({
				next: () => {
					this.notificationService.ShowToast(
						'Vanhenemispäivä päivitetty',
						'success',
					);

					// Update the local share object
					if (this.primaryShare) {
						this.primaryShare.expirationDate = newExpirationDate;
					}

					// Refresh shares from service
					if (this.activeShareProcess?.exportData.id !== undefined) {
						this.primaryShare = this.getPrimaryShare(
							this.activeShareProcess.exportData.id,
						);
						this.legacyShares = this.getLegacyShares(
							this.activeShareProcess.exportData.id,
						);
					}

					// Clear loading state and exit edit mode
					this.shareBeingUpdated = null;
					this.cancelEditingExpirationDate();
				},
				error: (error) => {
					console.error('Error updating expiration date:', error);
					this.notificationService.ShowToast(
						'Virhe vanhenemispäivän päivityksessä',
						'danger',
					);
					// Clear loading state on error
					this.shareBeingUpdated = null;
				},
			});
	}

	/**
	 * Check if an export is orphaned (shares exist but export doesn't)
	 */
	isOrphanedExport(exportId: number | undefined): boolean {
		if (exportId === undefined || exportId === null || exportId === -1)
			return false;
		return this.orphanedExportIds.has(exportId);
	}

	/**
	 * Compute orphaned export entries (shares whose parent exports no longer exist)
	 * This method is called when exports or shares change to rebuild the cache
	 */
	private computeOrphanedExports(): void {
		// Clear caches
		this.cachedOrphanedExports = [];
		this.orphanedExportIds.clear();

		// Get all experienceIds that exist in exports
		const existingExportIds = new Set(
			this.exports
				.map((exp) => exp.id)
				.filter((id) => id !== undefined && id !== -1),
		);

		// console.log(
		// 	'[computeOrphanedExports] Existing export IDs:',
		// 	Array.from(existingExportIds),
		// );
		// console.log(
		// 	'[computeOrphanedExports] Total shares:',
		// 	this.exportService.SharedExperiences.length,
		// );

		// Find shares whose parent export no longer exists
		const orphanedShares = this.exportService.SharedExperiences.filter(
			(share) => !existingExportIds.has(share.experienceId),
		);

		// console.log(
		// 	'[computeOrphanedExports] Orphaned shares found:',
		// 	orphanedShares.length,
		// 	orphanedShares.map((s) => ({
		// 		id: s.id,
		// 		experienceId: s.experienceId,
		// 		name: s.name,
		// 	})),
		// );

		// Group orphaned shares by experienceId
		const sharesByExperienceId = new Map<number, CreatedShare[]>();
		orphanedShares.forEach((share) => {
			if (!sharesByExperienceId.has(share.experienceId)) {
				sharesByExperienceId.set(share.experienceId, []);
			}
			sharesByExperienceId.get(share.experienceId)!.push(share);
		});

		// Create pseudo-Export objects for each orphaned experienceId
		sharesByExperienceId.forEach((shares, experienceId) => {
			// Sort shares by creation date (newest first) to get the most recent one
			const sortedShares = [...shares].sort(
				(a, b) => b.creationDate - a.creationDate,
			);
			const firstShare = sortedShares[0];

			const orphanedExport: Export = {
				id: experienceId,
				uuid: '', // No UUID for orphaned exports
				name: firstShare.name || `Kokemus ${experienceId}`,
				date: firstShare.creationDate || 0,
				creator: '', // Unknown creator
				version: 0,
				gameData: undefined as any, // No game data
			};

			this.cachedOrphanedExports.push(orphanedExport);
			this.orphanedExportIds.add(experienceId);
		});

		// console.log(
		// 	'[computeOrphanedExports] Orphaned export IDs in Set:',
		// 	Array.from(this.orphanedExportIds),
		// );
		// console.log(
		// 	'[computeOrphanedExports] Cached orphaned exports:',
		// 	this.cachedOrphanedExports.map((e) => ({ id: e.id, name: e.name })),
		// );
	}

	/**
	 * Get orphaned export entries (returns cached array, filtered by expiration toggle)
	 */
	get orphanedExportEntries(): Export[] {
		// If toggle is ON, return all orphaned exports
		if (this.showExpiredOrphanedShares) {
			return this.cachedOrphanedExports;
		}

		// If toggle is OFF, only return orphaned exports with at least one active (non-expired) share
		return this.cachedOrphanedExports.filter((orphanedExport) => {
			// Get all shares for this orphaned export
			const shares = this.exportService.SharedExperiences.filter(
				(share) => share.experienceId === orphanedExport.id,
			);

			// Check if at least one share is not expired
			return shares.some((share) => !this.isShareExpired(share));
		});
	}

	/**
	 * Get the display name for a plugin type
	 */
	getPluginTypeDisplayName(type: PluginType | undefined): string {
		if (!type) {
			return 'Tuntematon';
		}

		const gameDefinition =
			this.sourceMaterialService.GetGameTypeSettings(type);
		return gameDefinition?.title || String(type);
	}

	/**
	 * Fix a single export by opening migration modal with just that export
	 */
	fixSingleExport(exportData: Export): void {
		console.log(
			'[ExportsComponent] Opening migration modal for single export:',
			exportData.name,
		);

		// Create compatibility result for single export
		const compatibilityResult = [
			{
				export: exportData,
				isCompatible: false,
				migrationStatus: 'pending' as
					| 'pending'
					| 'migrating'
					| 'success'
					| 'failed',
				compatibilityError: this.getExportCompatibilityStatus(
					exportData.id,
				).error,
			},
		];

		// Open migration modal with single export
		this.openMigrationModal(compatibilityResult);
	}

	/**
	 * Check compatibility of all exports and open migration modal for incompatible ones
	 */
	checkAllExportsCompatibility(): void {
		if (this.exports.length === 0) {
			this.notificationService.ShowToast(
				'Ei pelejä tarkistettavaksi',
				'primary',
			);
			return;
		}

		console.log(
			'[ExportsComponent] DEBUG: Starting compatibility check for',
			this.exports.length,
			'exports',
		);
		this.notificationService.ShowToast(
			'Tarkistetaan pelien yhteensopivuutta...',
			'primary',
		);

		// Check each export's compatibility with error details
		const compatibilityChecks = this.exports.map((exp, index) => {
			console.log(`[ExportsComponent] DEBUG: Export ${index}:`, {
				name: exp.name,
				gameType: exp.gameData?.type,
				hasGameData: !!exp.gameData,
			});
			return {
				export: exp,
				check: this.migrationService.checkExportCompatibilityWithError(
					exp,
				),
			};
		});

		// Process results
		forkJoin(compatibilityChecks.map((item) => item.check)).subscribe({
			next: (results) => {
				console.log(
					'[ExportsComponent] DEBUG: Compatibility check results:',
					results,
				);

				// Create compatibility result objects
				const compatibilityResults = results.map((result, index) => {
					console.log(
						`[ExportsComponent] DEBUG: Export ${index} (${compatibilityChecks[index].export.name}) result:`,
						result,
					);

					return {
						export: compatibilityChecks[index].export,
						isCompatible: result.isCompatible,
						migrationStatus: 'pending' as
							| 'pending'
							| 'migrating'
							| 'success'
							| 'failed',
						compatibilityError: result.error,
					};
				});

				const incompatibleCount = compatibilityResults.filter(
					(result) => !result.isCompatible,
				).length;

				if (incompatibleCount === 0) {
					this.notificationService.ShowToast(
						'Kaikki pelit ovat yhteensopivia!',
						'success',
					);
				} else {
					console.log(
						'[ExportsComponent] DEBUG: Found',
						incompatibleCount,
						'incompatible exports',
					);
					this.notificationService.ShowToast(
						`Löytyi ${incompatibleCount} yhteensopimatonta peliä`,
						'warning',
					);

					// Open migration modal with all results
					this.openMigrationModal(compatibilityResults);
				}
			},
			error: (error) => {
				console.error(
					'[ExportsComponent] ERROR: Compatibility check failed:',
					error,
				);
				this.notificationService.ShowToast(
					'Virhe tarkistettaessa yhteensopivuutta',
					'danger',
				);
			},
		});
	}

	/**
	 * Get completed and failed unsaved batch jobs that can be treated like exports
	 * Excludes cancelled jobs (failed jobs with user cancellation error)
	 */
	get unsavedBatchJobs(): BatchJob[] {
		return this.batchGenerationService.getBatchJobs().filter(
			(job) =>
				job.status === 'completed' ||
				job.status === 'queued' ||
				job.status === 'processing' ||
				// Include failed jobs but exclude cancelled ones
				(job.status === 'failed' &&
					job.error &&
					!job.error.includes('Peruutettu')),
		);
	}

	/**
	 * Get progress for a specific batch job
	 * @param jobId Batch job ID
	 * @returns -2 for spinner (no progress), or 0-100 for progress wheel
	 */
	getJobProgress(jobId: string): number {
		// If job has progress in the map, return it
		if (this.batchJobProgressMap.has(jobId)) {
			return this.batchJobProgressMap.get(jobId) || 0;
		}
		// Default to spinner (no specific progress)
		return -2;
	}

	/**
	 * Save a batch job result as an export using modal pattern
	 * @param job Batch job to save
	 */
	saveBatchJob(job: BatchJob): void {
		if (!job.result) {
			this.notificationService.ShowToast(
				'Ei tulosta tallennettavaksi',
				'warning',
			);
			return;
		}

		// Start the export process using modal pattern
		this.startBatchJobExport(job.result, job).subscribe({
			next: (result: boolean) => {
				if (result) {
					// Remove from batch service only if export was successful
					this.batchGenerationService.deleteBatchJob(job.id);
					this.notificationService.ShowToast(
						`"${job.gameName}" tallennettu!`,
						'success',
					);
				}
			},
			error: (error: Error) => {
				console.error('Error saving batch job:', error);
				this.notificationService.ShowToast(
					`Tallennus epäonnistui: ${error.message}`,
					'danger',
				);
			},
		});
	}

	/**
	 * Start export process for batch job using modal pattern (similar to plugin-editor-base StartExport)
	 * @param exportObj Export object to save
	 * @param job Batch job for naming reference
	 * @returns Observable<boolean> indicating success
	 */
	private startBatchJobExport(exportObj: Export, job: BatchJob) {
		return defer(() => {
			// Open the export notification modal to get a name first
			const modalRef = this.modalService.open(
				ExportNotificationModalComponent,
				{
					centered: true,
					backdrop: 'static',
					keyboard: false,
				},
			);

			// Set default name and override date
			exportObj.date = Math.floor(Date.now() / 1000);

			// Listen for name submission
			const componentInstance =
				modalRef.componentInstance as ExportNotificationModalComponent;

			// Set default name based on job name
			componentInstance.exportName = job.gameName || '';

			// Variables to track publish intent and expiry timestamp
			let shouldPublish = false;
			let publishExpiryTimestamp = -1;

			// Subscribe to publishWithExpiry event to handle immediate publishing
			componentInstance.publishWithExpiry.subscribe(
				(expiryTimestamp: number) => {
					// User wants to publish - store both the flag and expiry timestamp
					shouldPublish = true;
					publishExpiryTimestamp = expiryTimestamp;
					console.log(
						'User wants to publish with expiry:',
						expiryTimestamp,
					);
				},
			);

			return componentInstance.nameSubmitted.pipe(
				tap((name: string) => {
					// Set the name on the export object
					exportObj.name = name;
				}),
				switchMap(() => {
					// Start the export process with the named export object
					return this.exportToExports(
						exportObj,
						componentInstance,
					).pipe(
						map((exportResult: Export) => {
							console.log('Export saved successfully');

							// If user wanted to publish, create share immediately
							if (
								shouldPublish &&
								exportResult.id !== undefined &&
								exportResult.gameData
							) {
								// Set isPublishing flag to keep loading state active
								componentInstance.isPublishing = true;
								componentInstance['cdr'].detectChanges();

								// Get filtered article IDs for external items
								const allArticleIds = Array.from(
									new Set(
										exportResult.gameData?.GetArticlesContainedInGameDataObjectsRecursively(
											true,
										) || [],
									),
								);
								const usedArticleIds =
									this.sourceMaterialService.filterExternalArticleIds(
										allArticleIds,
									);

								this.exportService.createShareForExport(
									exportResult,
									publishExpiryTimestamp,
									usedArticleIds,
								).subscribe({
									next: (createdShare: CreatedShare) => {
										console.log(
											'Share created successfully:',
											createdShare,
										);
										// Now update state to show completion
										componentInstance.updateState(
											false,
											true,
											false,
										);
										// Set the created share to display details
										componentInstance.isPublishing = false;
										componentInstance.createdShare =
											createdShare;
										componentInstance[
											'cdr'
										].detectChanges();
										this.notificationService.ShowToast(
											'Peli tallennettu ja julkaistu!',
											'success',
										);
									},
									error: (error: unknown) => {
										console.error(
											'Error creating share:',
											error,
										);
										// Update state to show completion (export succeeded even if publish failed)
										componentInstance.updateState(
											false,
											true,
											false,
										);
										// Clear isPublishing flag on error
										componentInstance.isPublishing = false;
										componentInstance[
											'cdr'
										].detectChanges();
										this.notificationService.ShowToast(
											'Peli tallennettu, mutta julkaisu epäonnistui',
											'warning',
										);
									},
								});
							}

							return true;
						}),
						catchError((error: Error) => {
							console.error('Error exporting game data:', error);
							return throwError(() => error);
						}),
					);
				}),
				// Clean up subscription when modal is closed
				tap(() => {
					modalRef.closed.subscribe(() => {
						// Cleanup handled by the observable completion
					});

					modalRef.dismissed.subscribe(() => {
						// Cleanup handled by the observable completion
					});
				}),
			);
		});
	}

	/**
	 * Export to exports using modal for progress (similar to plugin-editor-base ExportToExports)
	 * @param exportObject Export object to save
	 * @param modalComponent Modal component for progress display
	 * @returns Observable<Export>
	 */
	private exportToExports(
		exportObject: Export,
		modalComponent?: ExportNotificationModalComponent,
	) {
		if (!exportObject) {
			throw new Error('Export object is not defined');
		}

		let componentInstance: ExportNotificationModalComponent;

		if (modalComponent) {
			// Use the existing modal component
			componentInstance = modalComponent;
			componentInstance.updateState(true, false, false);
		} else {
			// This shouldn't happen in our use case, but included for completeness
			throw new Error('Modal component is required');
		}

		// Create a shared observable that caches the result
		const exportObs = this.exportService.SaveExport(exportObject).pipe(
			catchError((error: unknown) => {
				componentInstance.updateState(false, false, true);
				console.error('Error exporting game data:', error);
				return throwError(() => new Error('Export failed'));
			}),
			tap(() => {
				componentInstance.updateState(false, true, false);
			}),
			// shareReplay(1) caches the last emission and shares it with all subscribers
			shareReplay(1),
			tap({
				next: () => console.log('Export saved successfully'),
				error: (error) => console.error('Error saving export:', error),
			}),
		);

		// Ensure execution by subscribing internally
		exportObs.subscribe();

		return exportObs;
	}

	/**
	 * Cancel an in-progress batch job with confirmation and delete it
	 * @param job Batch job to cancel
	 */
	cancelAndDeleteBatchJob(job: BatchJob): void {
		const modalRef = this.modalService.open(ConfirmModalComponent);
		modalRef.componentInstance.title = 'Peruuta pelin luominen';
		modalRef.componentInstance.message = `Haluatko varmasti peruuttaa pelin "${job.gameName}" luomisen?`;
		modalRef.componentInstance.confirmButtonText = 'Peruuta luominen';
		modalRef.componentInstance.confirmButtonClass = 'btn-warning';

		modalRef.result.then((confirmed) => {
			if (!confirmed) return;

			// Mark job as being cancelled
			this.batchJobsBeingCancelled.add(job.id);

			this.batchGenerationService
				.cancelAndDeleteBatchJob(job.id)
				.subscribe({
					next: (success) => {
						// Remove from being cancelled set
						this.batchJobsBeingCancelled.delete(job.id);

						if (success) {
							console.log(
								`Batch job ${job.id} cancelled and deleted successfully`,
							);
						}
					},
					error: (error) => {
						// Remove from being cancelled set even on error
						this.batchJobsBeingCancelled.delete(job.id);

						console.error('Error cancelling batch job:', error);
						this.notificationService.ShowToast(
							`Virhe peruuttaessa pelin "${job.gameName}" luomista`,
							'danger',
						);
					},
				});
		});
	}

	/**
	 * Show confirmation dialog before deleting a batch job
	 * @param job Batch job to delete
	 * @param modalTemplate Template reference for the confirmation modal
	 */
	confirmDeleteBatchJob(
		job: BatchJob,
		modalTemplate: TemplateRef<unknown>,
	): void {
		const modalRef = this.modalService.open(modalTemplate, {
			centered: true,
		});

		modalRef.result
			.then((result) => {
				if (result === 'confirm') {
					this.deleteBatchJob(job);
				}
			})
			.catch(() => {
				// Modal was dismissed, do nothing
			});
	}

	/**
	 * Delete an unsaved batch job
	 * @param job Batch job to delete
	 */
	deleteBatchJob(job: BatchJob): void {
		this.batchGenerationService.deleteBatchJob(job.id);
		this.notificationService.ShowToast(
			`"${job.gameName}" poistettu`,
			'success',
		);
	}

	/**
	 * Play a batch job result (same as playing an export)
	 * @param job Batch job to play
	 */
	async playBatchJob(job: BatchJob): Promise<void> {
		console.debug('=== playBatchJob DEBUG START ===');
		console.debug('Batch Job Details:', {
			id: job.id,
			gameType: job.gameType,
			gameName: job.gameName,
			status: job.status,
			hasResult: !!job.result,
			createdAt: job.createdAt,
			completedAt: job.completedAt,
			parameters: job.parameters,
		});

		if (!job.result) {
			console.error('Batch job has no result data');
			this.notificationService.ShowToast(
				'Ei pelidataa toistettavaksi',
				'warning',
			);
			return;
		}

		console.debug('Batch job result (Export) before restoration:', {
			resultId: job.result.id,
			resultName: job.result.name,
			resultDate: job.result.date,
			resultCreator: job.result.creator,
			resultVersion: job.result.version,
			hasGameData: !!job.result.gameData,
			gameDataType: job.result.gameData?.type,
			gameDataConstructor: job.result.gameData?.constructor?.name,
		});

		// Restore GameData methods and prototypes for batch job results
		// This is critical because batch jobs serialize/deserialize game data, losing prototype methods
		if (job.result.gameData) {
			try {
				console.debug(
					'Restoring prototype methods for batch job gameData...',
				);

				// Use RendererGameDefinitionService to get the constructor
				const gameDefinitionService =
					new RendererGameDefinitionService();
				const gameDataConstructor =
					await gameDefinitionService.getGameDataConstructor(
						job.result.gameData.type,
					);

				if (gameDataConstructor) {
					console.debug(
						'GameData constructor found, adding methods...',
					);
					GameData.addMethods(
						job.result.gameData,
						gameDataConstructor,
					);

					// Build the GameData types map for nested game data objects
					const availableTypes =
						gameDefinitionService.AvailablePluginTypes;
					const gameDataTypesMap = new Map<
						PluginType,
						new () => GameData
					>();

					for (const pluginType of availableTypes) {
						try {
							const constructor =
								await gameDefinitionService.getGameDataConstructor(
									pluginType,
								);
							if (constructor) {
								gameDataTypesMap.set(pluginType, constructor);
							}
						} catch (error) {
							console.debug(
								`Could not load constructor for plugin type ${pluginType}:`,
								error,
							);
						}
					}

					// Restore methods for any nested GameData objects
					if (job.result.gameData.GetContainedLocalGameDataObjects) {
						const nestedObjects =
							job.result.gameData.GetContainedLocalGameDataObjects();
						console.debug(
							`Found ${nestedObjects.length} nested GameData objects to restore`,
						);

						for (const nestedGameData of nestedObjects) {
							const nestedConstructor = gameDataTypesMap.get(
								nestedGameData.type,
							);
							if (nestedConstructor) {
								GameData.addMethods(
									nestedGameData,
									nestedConstructor,
								);
								console.debug(
									`Restored methods for nested ${nestedGameData.constructor.name}`,
								);
							}
						}
					}

					console.debug(
						'Prototype restoration completed successfully',
					);
					console.debug('GameData after restoration:', {
						constructorName: job.result.gameData.constructor.name,
						hasGetUsedArticles:
							typeof job.result.gameData.GetUsedArticles ===
							'function',
						hasGetContainedLocalGameDataObjects:
							typeof job.result.gameData
								.GetContainedLocalGameDataObjects ===
							'function',
					});
				} else {
					console.warn(
						'Could not find GameData constructor for type:',
						job.result.gameData.type,
					);
				}
			} catch (error) {
				console.error(
					'Error restoring GameData prototype methods:',
					error,
				);
				// Continue with playback attempt even if restoration fails
			}
		}

		console.debug('Calling PlayExport with restored batch job result...');
		// Play using the same method as exports
		await this.PlayExport(job.result);
		console.debug('=== playBatchJob DEBUG END ===');
	}

	/**
	 * Edit a batch job result (same as editing an export)
	 * @param job Batch job to edit
	 */
	async editBatchJob(job: BatchJob): Promise<void> {
		if (!job.result) {
			this.notificationService.ShowToast(
				'Ei pelidataa muokattavaksi',
				'warning',
			);
			return;
		}

		// Edit using the same method as exports
		await this.EditExport(job.result);

		// Subscribe to export completion with direct job reference
		if (this.localModalRef && this.localModalRef.componentInstance) {
			const componentInstance = this.localModalRef.componentInstance;

			// Check if the component has exportCompleted event
			if (componentInstance.exportCompleted) {
				// Subscribe to exportCompleted event with the job captured in closure
				const subscription =
					componentInstance.exportCompleted.subscribe(() => {
						console.log(
							`Export completed for batch job ${job.id}, removing from unsaved batch jobs`,
						);
						// Remove the batch job from the service using direct job reference
						this.batchGenerationService.deleteBatchJob(job.id);
						// Unsubscribe to prevent memory leaks
						subscription.unsubscribe();
					});

				// Also clean up subscription when modal closes
				this.localModalRef.closed.subscribe(() => {
					subscription.unsubscribe();
				});
				this.localModalRef.dismissed.subscribe(() => {
					subscription.unsubscribe();
				});
			}
		}
	}

	/**
	 * Get filtered exports based on search text
	 */
	get filteredExports(): Export[] {
		let filtered = this.exports;

		// Apply search filter if there's search text
		if (this.searchText.trim()) {
			const searchTerm = this.searchText.toLowerCase().trim();

			filtered = filtered.filter((exportData) => {
				// Search in export name
				const exportName = exportData.name?.toLowerCase() || '';
				if (exportName.includes(searchTerm)) return true;

				// Search in export ID
				const exportId = exportData.id?.toString().toLowerCase() || '';
				if (exportId.includes(searchTerm)) return true;

				// Search in creator
				const creator = exportData.creator?.toLowerCase() || '';
				if (creator.includes(searchTerm)) return true;

				// Search in UUID
				const uuid = exportData.uuid?.toLowerCase() || '';
				if (uuid.includes(searchTerm)) return true;

				// Search in game type
				// const gameType =
				// 	exportData.gameData?.type?.toString().toLowerCase() || '';
				// if (gameType.includes(searchTerm)) return true;

				return false;
			});
		}

		// Merge normal exports with orphaned export entries before filtering and sorting
		filtered = [...filtered, ...this.orphanedExportEntries];

		// Apply share status filter
		if (!this.showNotShared || !this.showShared) {
			filtered = filtered.filter((exportData) => {
				const shareCount = this.getShareCount(exportData.id);
				const isNotShared = shareCount === 0;
				const isShared = shareCount > 0;

				return (
					(this.showNotShared && isNotShared) ||
					(this.showShared && isShared)
				);
			});
		}

		// Apply archive filter (switch between non-archived and archived exports)
		// When showArchived is false: show only non-archived exports
		// When showArchived is true: show only archived exports
		filtered = filtered.filter((exportData) => {
			const isArchived = this.exportService.isExportArchived(
				exportData.id,
			);
			return this.showArchived ? isArchived : !isArchived;
		});

		// Apply sorting to all exports (normal + orphaned)
		return this.applySorting(filtered);
	}

	/**
	 * Sorts the exports by the specified column
	 */
	sortExports(column: 'name' | 'date' | 'type'): void {
		if (this.sortColumn === column) {
			// Toggle direction if clicking same column
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			// Set new column and reset to ascending
			this.sortColumn = column;
			this.sortDirection = 'asc';
		}
	}

	/**
	 * Update share status filters (filtering is handled by filteredExports getter)
	 */
	updateShareFilters(): void {
		// Filter logic handled by filteredExports getter
	}

	/**
	 * Applies sorting to the given exports array
	 */
	private applySorting(exports: Export[]): Export[] {
		if (!this.sortColumn) {
			return exports;
		}

		return [...exports].sort((a, b) => {
			let aValue: string | number;
			let bValue: string | number;

			switch (this.sortColumn) {
				case 'name':
					aValue = a.name.toLowerCase();
					bValue = b.name.toLowerCase();
					break;
				case 'date':
					// date is a Unix timestamp (seconds)
					aValue = Number(a.date);
					bValue = Number(b.date);
					break;
				case 'type':
					// Sort by the displayed plugin type name alphabetically
					aValue = this.getPluginTypeDisplayName(
						a.gameData?.type,
					).toLowerCase();
					bValue = this.getPluginTypeDisplayName(
						b.gameData?.type,
					).toLowerCase();
					break;
				default:
					return 0;
			}

			let comparison = 0;
			if (aValue < bValue) {
				comparison = -1;
			} else if (aValue > bValue) {
				comparison = 1;
			}

			return this.sortDirection === 'asc' ? comparison : -comparison;
		});
	}

	/**
	 * Gets the sort icon class for a column
	 */
	getSortIcon(column: 'name' | 'date' | 'type'): string {
		if (this.sortColumn !== column) {
			return 'bi-arrow-down-up'; // Default sort icon
		}

		if (column === 'name' || column === 'type') {
			// Alphabetical sorting for name and type columns
			return this.sortDirection === 'asc'
				? 'bi-sort-alpha-down'
				: 'bi-sort-alpha-up';
		} else {
			// Numeric sorting for date column
			return this.sortDirection === 'asc'
				? 'bi-sort-numeric-down'
				: 'bi-sort-numeric-up';
		}
	}

	// Extract build number from appVersion (x.y.build_number)
	getBuildNumber(): string {
		const versionParts = environment.appVersion.split('.');
		return versionParts.length >= 3 ? versionParts[2] : '0';
	}

	/**
	 * Load all shares for the specified export
	 */
	private loadSharesForExport(exportId: number): void {
		// Allow 0 as a valid exportId (for pseudo exports with experienceId: 0)
		if (exportId === undefined || exportId === null || exportId === -1) {
			this.currentExportShares = [];
			return;
		}

		// Get shares from export service
		this.currentExportShares =
			this.exportService.GetSharedExperiencesForExport(exportId);
	}

	/**
	 * Get filtered and sorted shares for the current export
	 */
	get filteredCurrentExportShares(): CreatedShare[] {
		const shares = [...this.currentExportShares];

		// Apply sorting
		return this.applySharesSorting(shares);
	}

	/**
	 * Sort shares by the specified column
	 */
	sortModalShares(column: 'name' | 'expirationDate' | 'creationDate'): void {
		if (this.sharesSortColumn === column) {
			// Toggle direction if clicking same column
			this.sharesSortDirection =
				this.sharesSortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			// Set new column and default to descending (newest first)
			this.sharesSortColumn = column;
			this.sharesSortDirection = 'desc';
		}
	}

	/**
	 * Apply sorting to shares array
	 */
	private applySharesSorting(shares: CreatedShare[]): CreatedShare[] {
		if (!this.sharesSortColumn) {
			// Default sort by expiration date descending (newest first)
			return shares.sort((a, b) => {
				const aDate = a.expirationDate || 0;
				const bDate = b.expirationDate || 0;
				return bDate - aDate;
			});
		}

		return shares.sort((a, b) => {
			let aValue: string | number;
			let bValue: string | number;

			switch (this.sharesSortColumn) {
				case 'name':
					// Use share name if available, otherwise use export name
					aValue = (
						a.name ||
						this.activeShareProcess?.exportData.name ||
						''
					).toLowerCase();
					bValue = (
						b.name ||
						this.activeShareProcess?.exportData.name ||
						''
					).toLowerCase();
					break;
				case 'expirationDate':
					// Sort by expiration date (Unix timestamp in seconds)
					aValue = Number(a.expirationDate || 0);
					bValue = Number(b.expirationDate || 0);
					break;
				case 'creationDate':
					// Sort by creation date (Unix timestamp in seconds)
					aValue = Number(a.creationDate || 0);
					bValue = Number(b.creationDate || 0);
					break;
				default:
					return 0;
			}

			let comparison = 0;
			if (aValue < bValue) {
				comparison = -1;
			} else if (aValue > bValue) {
				comparison = 1;
			}

			return this.sharesSortDirection === 'asc'
				? comparison
				: -comparison;
		});
	}

	/**
	 * Get sort icon for shares table
	 */
	getSharesSortIcon(
		column: 'name' | 'expirationDate' | 'creationDate',
	): string {
		if (this.sharesSortColumn !== column) {
			return 'bi-arrow-down-up'; // Default sort icon
		}
		if (column === 'expirationDate' || column === 'creationDate') {
			return this.sharesSortDirection === 'asc'
				? 'bi-sort-numeric-down'
				: 'bi-sort-numeric-up';
		}
		return this.sharesSortDirection === 'asc'
			? 'bi-sort-alpha-down'
			: 'bi-sort-alpha-up';
	}

	/**
	 * Delete a share from the modal
	 */
	deleteShareFromModal(share: CreatedShare): void {
		if (!share.id) return;

		// Add to being deleted set
		this.sharesBeingDeletedInModal.add(share.id);

		this.exportService.DeleteShare(share.id).subscribe({
			next: () => {
				// Remove from being deleted set
				this.sharesBeingDeletedInModal.delete(share.id);

				// Remove from current shares array
				this.currentExportShares = this.currentExportShares.filter(
					(s) => s.id !== share.id,
				);

				// Refresh primary and legacy shares after deletion
				if (this.activeShareProcess?.exportData.id) {
					this.primaryShare = this.getPrimaryShare(
						this.activeShareProcess.exportData.id,
					);
					this.legacyShares = this.getLegacyShares(
						this.activeShareProcess.exportData.id,
					);
				}

				this.notificationService.ShowToast(
					'Julkaisu päätetty',
					'success',
				);
			},
			error: (error: unknown) => {
				// Remove from being deleted set even on error
				this.sharesBeingDeletedInModal.delete(share.id);

				this.notificationService.ShowToast(
					'Virhe julkaisun päättämisessä',
					'danger',
				);
				console.error('Error deleting share from modal:', error);
			},
		});
	}

	/**
	 * Copy shared link to clipboard from modal
	 */
	copySharedLinkToClipboardFromModal(share: CreatedShare): void {
		const link = this.GetShareURL(share.uuid);
		this.copySharedLinkToClipboard(link);
	}

	/**
	 * Copy iframe embed code to clipboard from modal
	 */
	copyEmbedCodeToClipboardFromModal(share: CreatedShare): void {
		const embedCode = this.GetShareIFrameCode(share.uuid);
		this.copySharedLinkToClipboard(embedCode);
	}

	/**
	 * Preview/play a shared export from modal
	 */
	previewShareFromModal(share: CreatedShare): void {
		const shareUrl = this.GetShareURL(share.uuid);
		this.openSharedLinkInNewWindow(shareUrl);
	}

	/**
	 * Play a shared export from modal using download-first pattern
	 */
	playShareFromModal(share: CreatedShare): void {
		// Start loading
		this.sharesBeingLoadedInModal.add(share.id);

		// Get the shared experience data using public backend service
		this.publicDataService.GetExperienceByUuid(share.uuid).subscribe({
			next: async (experience) => {
				try {
					if (!experience) {
						this.notificationService.ShowToast(
							'Kokemusta ei löytynyt',
							'danger',
						);
						this.sharesBeingLoadedInModal.delete(share.id);
						return;
					}

					const gameData = JSON.parse(experience.jsonBlob);
					GameData.restoreMaps(null, gameData);

					if (!gameData || !gameData.type) {
						this.notificationService.ShowToast(
							'Pelidataa ei löydy',
							'danger',
						);
						this.sharesBeingLoadedInModal.delete(share.id);
						return;
					}

					// Find the renderer component for this game type using RendererGameDefinitionService
					const gameDefinitionService =
						new RendererGameDefinitionService();
					const gameDefinition =
						await gameDefinitionService.getGameDefinition(
							gameData.type,
						);

					if (!gameDefinition || !gameDefinition.rendererComponent) {
						this.notificationService.ShowToast(
							'Renderöijää ei löydy tälle pelityypille',
							'danger',
						);
						this.sharesBeingLoadedInModal.delete(share.id);
						return;
					}

					// Validate game data before opening preview
					const validationResult =
						await gameDefinitionService.validateGameDataForImport(
							gameData.type,
							gameData,
						);

					if (!validationResult.isValid) {
						// Show the specific validation error(s)
						const errorMessage =
							validationResult.errors.length > 0
								? `Validointivirhe: ${validationResult.errors.join(', ')}`
								: 'Sisällössä on validointivirheitä. Esikatselu ei ole mahdollinen.';

						this.notificationService.ShowToast(
							errorMessage,
							'danger',
						);
						this.sharesBeingLoadedInModal.delete(share.id);
						return;
					}

					// Create export-like object for the renderer
					const exportData: Export = {
						id: experience.id,
						uuid: experience.uuid,
						name: experience.name,
						date: experience.createdUnixTime,
						creator: experience.userId,
						version: experience.version,
						gameData: gameData,
					};

					// Set the default shareUuid in PublicDataServiceProvider for fallback scenarios
					this.publicDataService.SetDefaultShareUuid(share.uuid);

					// Load and open the renderer component
					const componentType =
						await gameDefinition.rendererComponent();

					const modalRef = this.modalService.open(componentType, {
						size: 'xl',
						windowClass: `dark-modal ${gameDefinition.displayMode == 'fullscreen' || (gameDefinition.displayMode == 'both' && gameDefinition.defaultDisplayMode == 'fullscreen') ? 'modal-xxl' : ''}`,
					});

					if (modalRef && modalRef.componentInstance) {
						// Set the export data for the renderer
						modalRef.componentInstance.exportData = exportData;

						// Pass shareId directly to renderer component for direct access
						modalRef.componentInstance.shareId = share.uuid;
					}

					// Loading completed successfully - modal is now open
					this.sharesBeingLoadedInModal.delete(share.id);
				} catch (error) {
					console.error('Error playing shared export:', error);
					this.notificationService.ShowToast(
						'Virhe toistaessa jaettua kokemusta',
						'danger',
					);
					this.sharesBeingLoadedInModal.delete(share.id);
				}
			},
			error: (error: unknown) => {
				console.error('Error loading shared experience:', error);
				this.notificationService.ShowToast(
					'Virhe ladattaessa jaettua kokemusta',
					'danger',
				);
				this.sharesBeingLoadedInModal.delete(share.id);
			},
		});
	}

	/**
	 * Check if a share is expired
	 */
	isShareExpired(share: CreatedShare): boolean {
		if (!share.expirationDate || share.expirationDate === -1) {
			return false; // No expiration date set
		}

		const now = Math.floor(Date.now() / 1000);
		return share.expirationDate <= now;
	}

	/**
	 * Refresh shares list after creating a new share
	 */
	private refreshSharesAfterCreation(): void {
		if (this.activeShareProcess?.exportData.id) {
			this.loadSharesForExport(this.activeShareProcess.exportData.id);
			// Also refresh primary and legacy shares
			this.primaryShare = this.getPrimaryShare(
				this.activeShareProcess.exportData.id,
			);
			this.legacyShares = this.getLegacyShares(
				this.activeShareProcess.exportData.id,
			);
		}
	}

	/**
	 * Reset share form after successful creation for next share
	 */
	private resetShareFormAfterCreation(): void {
		// Reset form fields for creating another share
		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate() + 1);

		this.expirationDatePicker = {
			year: tomorrow.getFullYear(),
			month: tomorrow.getMonth() + 1,
			day: tomorrow.getDate(),
		};
		this.expirationHour = 23;
		this.expirationMinute = 59;
		this.hasExpiryDate = false;

		// Reset the share process state but keep the export data
		if (this.activeShareProcess) {
			const exportData = this.activeShareProcess.exportData;
			this.activeShareProcess = new ShareProcess(exportData);
			this.activeShareProcess.expirationDate = -1;
		}
	}

	/**
	 * Open rename modal for an export
	 */
	openRenameModal(exportData: Export): void {
		// Initialize modal state
		this.activeRenameExport = exportData;
		this.renameName = exportData.name;
		this.renameValidationErrors = [];
		this.showRenameValidationErrors = false;

		// Open the modal
		this.renameModalRef = this.modalService.open(this.renameModalTemplate, {
			centered: true,
			backdrop: 'static',
			keyboard: false,
		});

		// Handle modal close/dismiss
		this.renameModalRef.result.then(
			() => {
				// Closed
				this.resetRenameModalState();
				this.renameModalRef = undefined;
			},
			() => {
				// Dismissed
				this.resetRenameModalState();
				this.renameModalRef = undefined;
			},
		);
	}

	/**
	 * Reset rename modal state
	 */
	private resetRenameModalState(): void {
		this.activeRenameExport = null;
		this.renameName = '';
		this.renameValidationErrors = [];
		this.showRenameValidationErrors = false;
		this.isRenamingInProgress = false;
	}

	/**
	 * Sanitize file name by removing invalid characters
	 */
	private sanitizeFileName(fileName: string): string {
		// Remove characters that are not allowed in file names
		// Allowed: letters, numbers, spaces, hyphens, underscores, parentheses, periods, colons
		return fileName.replace(/[^a-zA-Z0-9\s\-_().:åäöÅÄÖ]/g, '');
	}

	/**
	 * Validate the rename file name
	 */
	validateRenameFileName(): void {
		this.renameValidationErrors = [];
		const trimmedName = this.renameName.trim();

		// Check if empty
		if (!trimmedName) {
			this.renameValidationErrors.push(
				'Tallennuksen nimi ei voi olla tyhjä',
			);
			return;
		}

		// Check minimum length
		if (trimmedName.length < 2) {
			this.renameValidationErrors.push(
				'Tallennuksen nimi on liian lyhyt',
			);
			return;
		}

		// Check maximum length
		if (trimmedName.length > 100) {
			this.renameValidationErrors.push(
				'Tallennuksen nimi on liian pitkä (max 100 merkkiä)',
			);
			return;
		}

		// Check for invalid characters
		if (!/^[a-zA-Z0-9\s\-_().:åäöÅÄÖ]+$/.test(trimmedName)) {
			this.renameValidationErrors.push(
				'Tallennuksen nimessä on virheellisiä merkkejä',
			);
		}
	}

	/**
	 * Check if the rename name is valid
	 */
	isRenameNameValid(): boolean {
		this.validateRenameFileName();
		return this.renameValidationErrors.length === 0;
	}

	/**
	 * Handle input changes with real-time validation
	 */
	onRenameNameInput(): void {
		this.showRenameValidationErrors = true;
		this.validateRenameFileName();
	}

	/**
	 * Submit the rename
	 */
	submitRename(): void {
		// Validate before submitting
		if (!this.isRenameNameValid()) {
			this.showRenameValidationErrors = true;
			return;
		}

		if (!this.activeRenameExport) {
			console.error('No export selected for rename');
			this.notificationService.ShowToast(
				'Virhe: Ei valittua peliä',
				'danger',
			);
			return;
		}

		// Set renaming in progress to disable buttons
		this.isRenamingInProgress = true;

		// Sanitize the name
		const sanitizedName = this.sanitizeFileName(this.renameName.trim());

		// Create updated export object with new name
		const updatedExport: Export = {
			...this.activeRenameExport,
			name: sanitizedName,
		};

		// Call exportService.UpdateExport to update both backend and local state
		this.exportService.UpdateExport(updatedExport).subscribe({
			next: () => {
				this.notificationService.ShowToast(
					`Peli nimetty uudelleen: "${sanitizedName}"`,
					'success',
				);
				// Close the modal
				if (this.renameModalRef) {
					this.renameModalRef.close();
				}
			},
			error: (error: unknown) => {
				console.error('Error renaming export:', error);
				this.notificationService.ShowToast(
					'Virhe nimettäessä peliä uudelleen',
					'danger',
				);
				// Re-enable buttons on error
				this.isRenamingInProgress = false;
			},
		});
	}

	/**
	 * Open migration modal for exports (single export or list of compatibility results)
	 */
	openMigrationModal(data: Export | unknown[]): void {
		const modalRef = this.modalService.open(MigrationModalComponent, {
			size: 'xl', // Larger size for list view
			centered: true,
			backdrop: 'static',
			keyboard: false,
		});

		if (modalRef.componentInstance) {
			// Check if it's a single export (legacy) or list of compatibility results
			if (Array.isArray(data)) {
				modalRef.componentInstance.compatibilityResults = data;
			} else {
				// Legacy single export support - convert to list format
				modalRef.componentInstance.compatibilityResults = [
					{
						export: data,
						isCompatible: false,
						migrationStatus: 'pending' as
							| 'pending'
							| 'success'
							| 'failed',
					},
				];
			}
		}

		// Handle modal result
		modalRef.result.then(
			(result) => {
				// Modal was closed with success
				console.log('Migration modal closed with result:', result);
			},
			(reason) => {
				// Modal was dismissed
				console.log('Migration modal dismissed:', reason);
			},
		);
	}

	/**
	 * Handle mouse enter event on export row to show preview
	 * @param exportData Export to preview
	 * @param event Mouse event for positioning
	 */
	async onRowMouseEnter(
		exportData: Export,
		event: MouseEvent,
	): Promise<void> {
		// Clear any existing debounce timeout
		if (this.previewDebounceTimeout) {
			clearTimeout(this.previewDebounceTimeout);
			this.previewDebounceTimeout = null;
		}

		// Store the export
		this.previewExport = exportData;

		// Update position
		this.updatePreviewPosition(event);

		// Increment request ID to track this specific preview request
		const currentRequestId = ++this.previewRequestId;

		// Add a small debounce delay to prevent showing preview for quick mouse passes
		this.previewDebounceTimeout = setTimeout(async () => {
			// Check if this request is still current (user hasn't moved to another export)
			if (this.previewRequestId !== currentRequestId) {
				return;
			}

			// Show preview container
			this.showPreview = true;

			// Trigger change detection to render the container
			this.cdr.detectChanges();

			// Load the component
			await this.loadPreviewComponent(exportData, currentRequestId);
		}, 120); // 200ms debounce delay
	}

	/**
	 * Handle mouse move event to update preview position
	 * @param event Mouse event for positioning
	 */
	onRowMouseMove(event: MouseEvent): void {
		if (this.showPreview) {
			this.updatePreviewPosition(event);
		}
	}

	/**
	 * Handle mouse leave event to hide preview
	 */
	onRowMouseLeave(): void {
		// Cancel any pending debounce timeout
		if (this.previewDebounceTimeout) {
			clearTimeout(this.previewDebounceTimeout);
			this.previewDebounceTimeout = null;
		}

		// Increment request ID to cancel any ongoing async operations
		this.previewRequestId++;

		// Hide preview and reset state
		this.showPreview = false;
		this.previewExport = null;

		// Clean up the dynamically created component
		if (this.previewComponentRef) {
			this.previewComponentRef.destroy();
			this.previewComponentRef = null;
		}

		// Clear the container
		if (this.previewContainer) {
			this.previewContainer.clear();
		}
	}

	/**
	 * Update preview position based on mouse coordinates
	 * Positions the preview center-right of the cursor with viewport boundary checking
	 * @param event Mouse event
	 */
	private updatePreviewPosition(event: MouseEvent): void {
		// Position center-right of cursor with larger offset
		const offsetX = 80; // pixels to the right of cursor
		const previewWidth = 384; // Width of preview container
		const previewHeight = 216; // Height of preview container (30% of 720px)

		// Calculate initial position
		let x = event.clientX + offsetX;
		let y = event.clientY - previewHeight / 2; // Center vertically

		// Get viewport dimensions
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		// Boundary checking - prevent preview from going off-screen
		// Check right edge
		if (x + previewWidth > viewportWidth) {
			// Position to the left of cursor instead
			x = event.clientX - previewWidth - offsetX;
		}

		// Check left edge (in case it would go off-screen on the left)
		if (x < 0) {
			x = 10; // Small margin from left edge
		}

		// Check bottom edge
		if (y + previewHeight > viewportHeight) {
			y = viewportHeight - previewHeight - 10; // Small margin from bottom
		}

		// Check top edge
		if (y < 0) {
			y = 10; // Small margin from top
		}

		this.previewPosition = { x, y };
	}

	/**
	 * Calculate preview style for positioning
	 */
	get previewStyle(): any {
		return {
			left: `${this.previewPosition.x}px`,
			top: `${this.previewPosition.y}px`,
		};
	}

	/**
	 * Load and render the game component into the preview container
	 * @param exportData Export to preview
	 * @param requestId Request ID to check if this preview is still current
	 */
	private async loadPreviewComponent(
		exportData: Export,
		requestId: number,
	): Promise<void> {
		try {
			// Check if ViewChild is available
			if (!this.previewContainer) {
				await new Promise((resolve) => setTimeout(resolve, 0));
				if (!this.previewContainer) {
					console.error('[Preview] ViewChild not available');
					return;
				}
			}

			// Clear any existing preview component
			this.previewContainer.clear();

			if (!exportData.gameData) {
				return;
			}

			// Check if this request is still current
			if (this.previewRequestId !== requestId) {
				return;
			}

			// Restore GameData methods if needed
			if (
				exportData.gameData.constructor.name === 'Object' ||
				typeof exportData.gameData.GetUsedArticles !== 'function' ||
				typeof exportData.gameData.GetContainedLocalGameDataObjects !==
					'function'
			) {
				const gameDefinitionService =
					new RendererGameDefinitionService();
				const gameDataConstructor =
					await gameDefinitionService.getGameDataConstructor(
						exportData.gameData.type,
					);

				// Check again after async operation
				if (this.previewRequestId !== requestId) {
					return;
				}

				if (gameDataConstructor) {
					GameData.addMethods(
						exportData.gameData,
						gameDataConstructor,
					);

					// Build the GameData types map for nested game data objects
					const availableTypes =
						gameDefinitionService.AvailablePluginTypes;
					const gameDataTypesMap = new Map<
						PluginType,
						new () => GameData
					>();

					for (const pluginType of availableTypes) {
						try {
							const constructor =
								await gameDefinitionService.getGameDataConstructor(
									pluginType,
								);
							if (constructor) {
								gameDataTypesMap.set(pluginType, constructor);
							}
						} catch (error) {
							// Silently skip types that fail to load
						}
					}

					// Check again after async loop
					if (this.previewRequestId !== requestId) {
						return;
					}

					// Restore methods for ALL GameData objects (including nested ones)
					exportData.gameData.addPrototypesToAllGameData(
						gameDataTypesMap,
					);
				}
			}

			// Get the game type definition
			const gameTypeDefinition =
				this.sourceMaterialService.GetGameTypeSettings(
					exportData.gameData?.type,
				);

			if (
				gameTypeDefinition &&
				gameTypeDefinition.rendererComponent !== undefined
			) {
				// Dynamically load the component
				const componentType =
					await gameTypeDefinition.rendererComponent();

				// Check if request is still current
				if (this.previewRequestId !== requestId) {
					return;
				}

				if (!this.previewContainer) {
					return;
				}

				// Create component dynamically in the preview container
				// Pass the injector to ensure proper DI scope for nested components
				this.previewComponentRef =
					this.previewContainer.createComponent(componentType, {
						injector: this.injector,
					});

				// Set explicit dimensions on the host element for Shadow DOM
				const hostElement = this.previewComponentRef.location
					.nativeElement as HTMLElement;
				hostElement.style.width = '1280px';
				hostElement.style.height = '720px';
				hostElement.style.display = 'block';

				// Set the exportData on the component instance
				if (this.previewComponentRef.instance) {
					this.previewComponentRef.instance.exportData = exportData;

					// CRITICAL: Manually trigger change detection for the component
					// This ensures ngOnInit runs and the component properly initializes
					// This is especially important for components with Shadow DOM
					this.previewComponentRef.changeDetectorRef.detectChanges();

					// Give the component a moment to settle (especially important for Shadow DOM)
					// Check if request is still current before second detection
					setTimeout(() => {
						if (
							this.previewComponentRef &&
							this.previewRequestId === requestId
						) {
							this.previewComponentRef.changeDetectorRef.detectChanges();
						}
					}, 0);
				}
			}
		} catch (error) {
			console.error('Error loading preview component:', error);
		}
	}
}
